import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { getAuthHeader } from "../lib/auth";
import { openExternalUrl } from "../lib/appMode";

const TOURNAMENT_ID = "get-active-3v3-75-ovr-2026";
const CAPTAIN_REGISTRATION_DRAFT_KEY = `ga:tournament:${TOURNAMENT_ID}:captain-registration-draft`;
const INVITE_ACCEPTANCE_FLOW_KEY = `ga:tournament:${TOURNAMENT_ID}:invite-acceptance-flow`;

const TOURNAMENT_RULE_TABS = [
  { id: "rules", label: "Tournament Rules" },
  { id: "flow", label: "How It Works" },
  { id: "disputes", label: "Violations & Disputes" },
  { id: "match-day", label: "Match Day" },
];

const PARTICIPANT_EMAIL_PREVIEWS = {
  rules_prep: {
    label: "Rules & preparation",
    subject: "Get Active Tournament — rules, preparation, and match flow 🏀",
    intro: "Review the tournament format, 75 OVR cap, score reporting, disputes, and match coordination before competition begins.",
  },
  day_before: {
    label: "Day-before reminder",
    subject: "Get Active Tournament — final reminder before tournament day 🏀",
    intro: "Make sure your GAStreaming login works, your 75-or-lower build is ready, your roster is correct, and you know how to reach your private match lobby.",
  },
  day_of: {
    label: "Tournament-day reminder",
    subject: "🏀 The Get Active Tournament starts today",
    intro: "Sign in to GAStreaming, open the tournament page, find your live matchup, and use the private match lobby to coordinate Park invites with your opponent.",
  },
};

function safeJsonParse(value) {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function readSessionJson(key) {
  try {
    const raw = window.sessionStorage.getItem(key);
    return raw ? safeJsonParse(raw) : null;
  } catch {
    return null;
  }
}

function writeSessionJson(key, value) {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Registration can still continue without draft persistence.
  }
}

function clearSessionKey(key) {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // Ignore storage failures.
  }
}

function formatTournamentDate(value) {
  if (!value) return "To Be Announced";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "To Be Announced";
  return date.toLocaleString([], {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function toDateTimeLocalValue(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (number) => String(number).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function getSignedInUser() {
  try {
    const raw = localStorage.getItem("ga_user") || localStorage.getItem("user") || "";
    return raw ? safeJsonParse(raw) : null;
  } catch {
    return null;
  }
}

function apiUrl(path) {
  const base = String(api.base || "/api").replace(/\/$/, "");
  const suffix = String(path || "").startsWith("/") ? path : `/${path}`;
  return `${base}${suffix}`;
}

async function requestJSON(path, options = {}) {
  const response = await fetch(apiUrl(path), {
    headers: {
      Accept: "application/json",
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...(options.auth ? { Authorization: getAuthHeader() } : {}),
      ...(options.headers || {}),
    },
    method: options.method || "GET",
    body: options.body ? JSON.stringify(options.body) : undefined,
    mode: "cors",
    credentials: "omit",
  });

  const text = await response.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }

  if (!response.ok) {
    const message = data?.message || data?.error || `Request failed (${response.status})`;
    const error = new Error(message);
    error.status = response.status;
    error.data = data;
    throw error;
  }

  return data;
}

function statusLabel(status) {
  const labels = {
    awaiting_teammates: "Awaiting teammates",
    ready_for_review: "Ready for approval",
    payout_setup_required: "Payout setup required",
    needs_roster_fix: "Roster needs attention",
    approved: "Approved",
    waitlisted: "Waitlisted",
    rejected: "Rejected",
    cancelled: "Cancelled",
    withdrawn: "Withdrawn",
  };
  return labels[status] || "Registration pending";
}

function memberStatusLabel(status) {
  if (status === "accepted") return "Confirmed";
  if (status === "declined") return "Declined";
  return "Awaiting response";
}

function payoutEligibilityLabel(eligibility) {
  if (eligibility?.label) return eligibility.label;
  const status = String(eligibility?.status || "").toLowerCase();
  if (status === "payout_ready") return "Payout Ready";
  if (status === "verification_pending") return "Verification Pending";
  if (status === "setup_incomplete") return "Setup Incomplete";
  if (status === "action_required") return "Action Required";
  return "Stripe Setup Required";
}

function payoutEligibilityReady(eligibility) {
  return Boolean(eligibility?.ready || String(eligibility?.status || "").toLowerCase() === "payout_ready");
}

function matchReportStatusLabel(status) {
  const labels = {
    awaiting_opponent_submission: "Waiting for the other captain",
    pending_confirmation: "Waiting for the other captain",
    live_confirmed: "Live score confirmed — series still active",
    disputed: "Conflicting captain results — admin review required",
    confirmed: "Confirmed and locked",
    admin_resolved: "Resolved by admin",
  };
  return labels[status] || "Result pending";
}

const BRACKET_SLOT_Y = [20.4, 30.8, 41.4, 52.4, 62.9, 73.8, 84.3, 95.0];
const BRACKET_ROUND_TWO_Y = [29.8, 51.2, 72.0, 93.1];
const BRACKET_ROUND_THREE_Y = [41.5, 83.2];
const SEED_TO_VISUAL_SLOT = { 1: 0, 8: 1, 4: 2, 5: 3, 3: 4, 6: 5, 2: 6, 7: 7 };
const FIRST_ROUND_MATCH_IDS = {
  east: ["east-r1-1", "east-r1-2", "east-r1-3", "east-r1-4"],
  west: ["west-r1-1", "west-r1-2", "west-r1-3", "west-r1-4"],
};
const ROUND_TWO_MATCH_IDS = {
  east: ["east-r2-1", "east-r2-2"],
  west: ["west-r2-1", "west-r2-2"],
};

function buildBracketPlacements(teams = []) {
  const approved = (Array.isArray(teams) ? teams : [])
    .filter((team) => ["approved", "eliminated", "champion"].includes(team?.status))
    .slice()
    .sort((a, b) => Number(a?.approvedAt || a?.updatedAt || a?.createdAt || 0) - Number(b?.approvedAt || b?.updatedAt || b?.createdAt || 0));

  const placements = { east: {}, west: {} };
  const used = { east: new Set(), west: new Set() };
  const unassigned = [];

  approved.forEach((team) => {
    const conference = String(team?.conference || "").toLowerCase();
    const seed = Number(team?.seed);
    if (["east", "west"].includes(conference) && seed >= 1 && seed <= 8 && !used[conference].has(seed)) {
      placements[conference][seed] = team;
      used[conference].add(seed);
    } else {
      unassigned.push(team);
    }
  });

  unassigned.forEach((team) => {
    const eastCount = used.east.size;
    const westCount = used.west.size;
    let conference = eastCount <= westCount ? "east" : "west";
    if (used[conference].size >= 8) conference = conference === "east" ? "west" : "east";
    if (used[conference].size >= 8) return;

    const seed = [1, 2, 3, 4, 5, 6, 7, 8].find((candidate) => !used[conference].has(candidate));
    if (!seed) return;
    placements[conference][seed] = team;
    used[conference].add(seed);
  });

  return placements;
}

function matchById(matches, id) {
  return (Array.isArray(matches) ? matches : []).find((match) => match?.id === id) || null;
}

function BracketTeamRow({ team, score = 0, seed = null, compact = false, mirrored = false }) {
  if (!team) return null;

  const name = (
    <span
      style={{
        ...styles.bracketNameWrap,
        ...(mirrored ? styles.bracketNameWrapMirrored : {}),
      }}
    >
      {mirrored ? (
        <>
          <span style={compact ? styles.bracketCompactTeamText : styles.bracketTeamText}>{team.name}</span>
          {seed ? <span style={styles.bracketSeed}>#{seed}</span> : null}
        </>
      ) : (
        <>
          {seed ? <span style={styles.bracketSeed}>#{seed}</span> : null}
          <span style={compact ? styles.bracketCompactTeamText : styles.bracketTeamText}>{team.name}</span>
        </>
      )}
    </span>
  );

  const scoreNode = (
    <span style={compact ? styles.bracketCompactScore : styles.bracketScore}>
      {Number(score || 0)}
    </span>
  );

  return (
    <div
      style={{
        ...(compact ? styles.bracketCompactRow : styles.bracketTeamRow),
        ...(mirrored ? styles.bracketTeamRowMirrored : {}),
      }}
    >
      {mirrored ? (
        <>
          {scoreNode}
          {name}
        </>
      ) : (
        <>
          {name}
          {scoreNode}
        </>
      )}
    </div>
  );
}

function BracketParticipantSlot({ team, score = 0, conference, top, left, seed = null }) {
  if (!team) return null;
  return (
    <div
      style={{
        ...styles.bracketTeamSlot,
        top: `${top}%`,
        left,
      }}
      title={`${conference.toUpperCase()}${seed ? ` #${seed}` : ""} — ${team.name}`}
    >
      <BracketTeamRow
        team={team}
        score={score}
        seed={seed}
        mirrored={conference === "west"}
      />
    </div>
  );
}

function BracketConferenceFinal({ match, conference }) {
  if (!match?.teamA && !match?.teamB) return null;
  return (
    <div
      style={{
        ...styles.bracketFinalLayer,
        left: conference === "east" ? "28.3%" : "62.6%",
      }}
      title={`${conference.toUpperCase()} conference final`}
    >
      <div style={{ ...styles.bracketFinalParticipant, top: `${BRACKET_ROUND_THREE_Y[0]}%` }}>
        <BracketTeamRow
          team={match?.teamA}
          score={match?.scoreA || 0}
          compact
          mirrored={conference === "west"}
        />
      </div>
      <div style={{ ...styles.bracketFinalParticipant, top: `${BRACKET_ROUND_THREE_Y[1]}%` }}>
        <BracketTeamRow
          team={match?.teamB}
          score={match?.scoreB || 0}
          compact
          mirrored={conference === "west"}
        />
      </div>
    </div>
  );
}

function BracketConferenceChampion({ match, conference }) {
  if (!match?.teamA && !match?.teamB) return null;

  const team = conference === "east" ? match?.teamA : match?.teamB;
  const score = conference === "east" ? match?.scoreA || 0 : match?.scoreB || 0;
  if (!team) return null;

  return (
    <div
      style={{
        ...styles.bracketChampionSlot,
        left: conference === "east" ? "33.45%" : "57.7%",
      }}
      title={`${conference.toUpperCase()} champion`}
    >
      <BracketTeamRow
        team={team}
        score={score}
        compact
        mirrored={conference === "west"}
      />
    </div>
  );
}

function BracketOverlay({ matches = [], fallbackPlacements }) {
  const hasMatchModel = Array.isArray(matches) && matches.length > 0;

  if (!hasMatchModel) {
    return (
      <div style={styles.bracketOverlay} aria-label="Approved tournament teams">
        {["east", "west"].flatMap((conference) =>
          [1, 2, 3, 4, 5, 6, 7, 8].map((seed) => {
            const visualSlot = SEED_TO_VISUAL_SLOT[seed];
            const team = fallbackPlacements?.[conference]?.[seed];
            return (
              <BracketParticipantSlot
                key={`${conference}-${seed}`}
                team={team}
                score={0}
                seed={seed}
                conference={conference}
                top={BRACKET_SLOT_Y[visualSlot]}
                left={conference === "east" ? "3.3%" : "88.3%"}
              />
            );
          })
        )}
      </div>
    );
  }

  const nodes = [];

  ["east", "west"].forEach((conference) => {
    FIRST_ROUND_MATCH_IDS[conference].forEach((matchId, matchIndex) => {
      const match = matchById(matches, matchId);
      if (!match) return;
      const left = conference === "east" ? "3.3%" : "88.3%";
      const baseSlot = matchIndex * 2;
      nodes.push(
        <BracketParticipantSlot
          key={`${matchId}-a`}
          team={match.teamA}
          score={match.scoreA}
          seed={match.teamA?.seed}
          conference={conference}
          top={BRACKET_SLOT_Y[baseSlot]}
          left={left}
        />
      );
      nodes.push(
        <BracketParticipantSlot
          key={`${matchId}-b`}
          team={match.teamB}
          score={match.scoreB}
          seed={match.teamB?.seed}
          conference={conference}
          top={BRACKET_SLOT_Y[baseSlot + 1]}
          left={left}
        />
      );
    });

    ROUND_TWO_MATCH_IDS[conference].forEach((matchId, matchIndex) => {
      const match = matchById(matches, matchId);
      if (!match) return;
      const left = conference === "east" ? "15.5%" : "75.0%";
      const baseSlot = matchIndex * 2;
      nodes.push(
        <BracketParticipantSlot
          key={`${matchId}-a`}
          team={match.teamA}
          score={match.scoreA}
          conference={conference}
          top={BRACKET_ROUND_TWO_Y[baseSlot]}
          left={left}
        />
      );
      nodes.push(
        <BracketParticipantSlot
          key={`${matchId}-b`}
          team={match.teamB}
          score={match.scoreB}
          conference={conference}
          top={BRACKET_ROUND_TWO_Y[baseSlot + 1]}
          left={left}
        />
      );
    });
  });

  nodes.push(
    <BracketConferenceFinal key="east-final" match={matchById(matches, "east-final")} conference="east" />
  );
  nodes.push(
    <BracketConferenceFinal key="west-final" match={matchById(matches, "west-final")} conference="west" />
  );

  const championshipMatch = matchById(matches, "championship");
  nodes.push(
    <BracketConferenceChampion
      key="east-champion"
      match={championshipMatch}
      conference="east"
    />
  );
  nodes.push(
    <BracketConferenceChampion
      key="west-champion"
      match={championshipMatch}
      conference="west"
    />
  );

  return (
    <div style={styles.bracketOverlay} aria-label="Live tournament bracket and series scores">
      {nodes}
    </div>
  );
}

export default function Tournaments() {
  const user = useMemo(() => getSignedInUser(), []);
  const signedIn = Boolean(user?.email && getAuthHeader());

  const [tournament, setTournament] = useState(null);
  const [myTournament, setMyTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionBusy, setActionBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    teamName: "",
    platform: "",
    captainGamertag: "",
    teammateOne: "",
    teammateTwo: "",
  });
  const [scoreDrafts, setScoreDrafts] = useState({});
  const [captainScoreDraft, setCaptainScoreDraft] = useState({ scoreA: 0, scoreB: 0 });
  const [captainReportFeedback, setCaptainReportFeedback] = useState("");
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [disputeSubmitting, setDisputeSubmitting] = useState(false);
  const [disputeFiles, setDisputeFiles] = useState([]);
  const [disputeForm, setDisputeForm] = useState({
    reasonType: "incorrect_score",
    explanation: "",
  });
  const [inviteDetails, setInviteDetails] = useState({});
  const [activeInviteFlowTeamId, setActiveInviteFlowTeamId] = useState("");
  const [registrationResumeReady, setRegistrationResumeReady] = useState(false);
  const [playerDetailsDraft, setPlayerDetailsDraft] = useState({ gamertag: "", platform: "" });
  const [playerDetailsBusy, setPlayerDetailsBusy] = useState(false);
  const [lobbyOpen, setLobbyOpen] = useState(false);
  const [lobbyData, setLobbyData] = useState(null);
  const [lobbyLoading, setLobbyLoading] = useState(false);
  const [lobbyError, setLobbyError] = useState("");
  const [lobbyMessage, setLobbyMessage] = useState("");
  const [championOverlayDismissed, setChampionOverlayDismissed] = useState(false);
  const [payoutBusy, setPayoutBusy] = useState(false);
  const [payoutStatusMessage, setPayoutStatusMessage] = useState("");
  const [rulesOpen, setRulesOpen] = useState(false);
  const [rulesTab, setRulesTab] = useState("rules");
  const [participantEmailBusy, setParticipantEmailBusy] = useState("");
  const [participantEmailPreview, setParticipantEmailPreview] = useState("");
  const [scheduleDraft, setScheduleDraft] = useState("");

  const loadTournament = useCallback(async () => {
    const data = await requestJSON(`/tournaments/${TOURNAMENT_ID}`);
    setTournament(data?.tournament || null);
  }, []);

  const loadMine = useCallback(async () => {
    if (!signedIn) {
      setMyTournament(null);
      return;
    }
    const data = await requestJSON(`/tournaments/${TOURNAMENT_ID}/me`, { auth: true });
    setMyTournament(data || null);
  }, [signedIn]);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      await Promise.all([loadTournament(), loadMine()]);
    } catch (err) {
      setError(err.message || "Could not load tournament registration right now.");
    } finally {
      setLoading(false);
    }
  }, [loadMine, loadTournament]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  useEffect(() => {
    setScheduleDraft(toDateTimeLocalValue(tournament?.scheduledAt));
  }, [tournament?.scheduledAt]);

  useEffect(() => {
    const savedDraft = readSessionJson(CAPTAIN_REGISTRATION_DRAFT_KEY);
    if (savedDraft && typeof savedDraft === "object") {
      setForm((current) => ({
        ...current,
        teamName: String(savedDraft.teamName || ""),
        platform: String(savedDraft.platform || ""),
        captainGamertag: String(savedDraft.captainGamertag || ""),
        teammateOne: String(savedDraft.teammateOne || ""),
        teammateTwo: String(savedDraft.teammateTwo || ""),
      }));
    }

    const savedInviteFlow = readSessionJson(INVITE_ACCEPTANCE_FLOW_KEY);
    if (savedInviteFlow?.teamId) {
      const teamId = String(savedInviteFlow.teamId);
      setActiveInviteFlowTeamId(teamId);
      if (savedInviteFlow.details && typeof savedInviteFlow.details === "object") {
        setInviteDetails((current) => ({ ...current, [teamId]: savedInviteFlow.details }));
      }
    }
  }, []);

  useEffect(() => {
    if (!signedIn) return undefined;
    const params = new URLSearchParams(window.location.search || "");
    const connectReturn = params.get("connect");
    if (!connectReturn) return undefined;

    const flow = String(params.get("flow") || "general").toLowerCase();
    const flowTeamId = String(params.get("teamId") || readSessionJson(INVITE_ACCEPTANCE_FLOW_KEY)?.teamId || "");
    let cancelled = false;

    (async () => {
      setPayoutBusy(true);
      setPayoutStatusMessage("");
      try {
        const status = await requestJSON(`/connect/status`, { auth: true });
        if (cancelled) return;
        await requestJSON(`/tournaments/${TOURNAMENT_ID}/payout-eligibility/refresh`, { method: "POST", auth: true });
        const payoutReady = Boolean(status?.onboardingComplete && status?.payoutsEnabled);

        if (flow === "captain") {
          setRegistrationResumeReady(payoutReady);
          setPayoutStatusMessage(
            payoutReady
              ? "Payout setup confirmed. Your saved team registration is ready to finish below."
              : "Stripe payout setup still needs attention. Continue onboarding before creating the team."
          );
        } else if (flow === "invite") {
          if (flowTeamId) setActiveInviteFlowTeamId(flowTeamId);
          setPayoutStatusMessage(
            payoutReady
              ? "Payout setup confirmed. Finish your gamertag and platform details to confirm this roster spot."
              : "Stripe payout setup still needs attention. Continue onboarding before confirming this roster spot."
          );
        } else {
          setPayoutStatusMessage(
            payoutReady
              ? "Payout setup confirmed. You are tournament payout-ready and can fully qualify for prize brackets."
              : "Payout setup still needs attention. Continue Stripe Connect onboarding before your team can be approved."
          );
        }

        await loadMine();
        window.setTimeout(() => {
          const target = flow === "captain" ? "captain-registration" : flow === "invite" ? "team-invitations" : "team-eligibility";
          document.getElementById(target)?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 120);
      } catch (err) {
        if (!cancelled) setPayoutStatusMessage(err.message || "Could not refresh payout setup status.");
      } finally {
        if (!cancelled) setPayoutBusy(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loadMine, signedIn]);

  function updateForm(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function saveCaptainRegistrationDraft() {
    writeSessionJson(CAPTAIN_REGISTRATION_DRAFT_KEY, {
      ...form,
      savedAt: Date.now(),
    });
  }

  function saveInviteFlow(teamId) {
    writeSessionJson(INVITE_ACCEPTANCE_FLOW_KEY, {
      teamId,
      details: inviteDetails?.[teamId] || {},
      savedAt: Date.now(),
    });
  }

  async function createTeam(event) {
    event?.preventDefault?.();
    setError("");
    setMessage("");

    if (!myPayoutReady) {
      saveCaptainRegistrationDraft();
      setRegistrationResumeReady(false);
      setPayoutStatusMessage("Your team details are saved. Complete Stripe payout setup, then return here to finish creating the team.");
      await startPayoutSetup({ flow: "captain", preserveDraft: true });
      return;
    }

    setActionBusy(true);
    try {
      const data = await requestJSON(`/tournaments/${TOURNAMENT_ID}/teams`, {
        method: "POST",
        auth: true,
        body: {
          teamName: form.teamName,
          platform: form.platform,
          captainGamertag: form.captainGamertag,
          teammateUsernames: [form.teammateOne, form.teammateTwo],
        },
      });
      const siteNotificationsSent = Number(data?.delivery?.siteNotificationsSent || 0);
      const emailsSent = Number(data?.delivery?.emailsSent || 0);
      setMessage(
        siteNotificationsSent === 2 && emailsSent === 2
          ? "Team created. Both teammates received an on-site notification and email invite."
          : siteNotificationsSent === 2
          ? "Team created. Both teammates received on-site notifications. Email delivery may still be processing or unavailable."
          : "Team created. Your teammates can still open the tournament page to accept their pending invites."
      );
      clearSessionKey(CAPTAIN_REGISTRATION_DRAFT_KEY);
      setRegistrationResumeReady(false);
      setForm({ teamName: "", platform: "", captainGamertag: "", teammateOne: "", teammateTwo: "" });
      await refreshAll();
    } catch (err) {
      if (err?.data?.error === "captain_payout_setup_required") {
        saveCaptainRegistrationDraft();
        setPayoutStatusMessage("Stripe payout eligibility changed or still needs attention. Finish setup before the team can be created.");
      }
      setError(err.message || "Could not create the team.");
    } finally {
      setActionBusy(false);
    }
  }

  async function beginInviteAcceptance(teamId) {
    setError("");
    setMessage("");
    setActiveInviteFlowTeamId(teamId);
    saveInviteFlow(teamId);

    if (!myPayoutReady) {
      setPayoutStatusMessage("Your invite is saved. Complete Stripe payout setup, then return to enter your gamertag and platform.");
      await startPayoutSetup({ flow: "invite", teamId });
      return;
    }

    window.setTimeout(() => {
      document.getElementById(`invite-details-${teamId}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 80);
  }

  async function respondToInvite(teamId, decision) {
    setActionBusy(true);
    setError("");
    setMessage("");
    try {
      const details = inviteDetails?.[teamId] || {};
      await requestJSON(`/tournaments/${TOURNAMENT_ID}/invites/${teamId}/respond`, {
        method: "POST",
        auth: true,
        body: {
          decision,
          gamertag: decision === "accept" ? details.gamertag || "" : "",
          platform: decision === "accept" ? details.platform || "" : "",
        },
      });
      setMessage(decision === "accept" ? "Roster spot confirmed. Your team can move forward once all three players are confirmed and payout-ready." : "Team invitation declined.");
      if (decision === "accept" || activeInviteFlowTeamId === teamId) {
        setActiveInviteFlowTeamId("");
        clearSessionKey(INVITE_ACCEPTANCE_FLOW_KEY);
      }
      await refreshAll();
    } catch (err) {
      if (err?.data?.error === "payout_setup_required") {
        setActiveInviteFlowTeamId(teamId);
        saveInviteFlow(teamId);
        setPayoutStatusMessage("Complete Stripe payout setup before confirming this tournament roster spot.");
      }
      setError(err.message || "Could not answer the team invitation.");
    } finally {
      setActionBusy(false);
    }
  }

  function updateInviteDetails(teamId, field, value) {
    setInviteDetails((current) => {
      const nextDetails = { ...(current?.[teamId] || {}), [field]: value };
      if (activeInviteFlowTeamId === teamId) {
        writeSessionJson(INVITE_ACCEPTANCE_FLOW_KEY, { teamId, details: nextDetails, savedAt: Date.now() });
      }
      return { ...current, [teamId]: nextDetails };
    });
  }

  async function saveMyPlayerDetails() {
    if (!myTeam?.id) return;
    setPlayerDetailsBusy(true);
    setError("");
    setMessage("");
    try {
      await requestJSON(`/tournaments/${TOURNAMENT_ID}/teams/${myTeam.id}/player-details`, {
        method: "POST",
        auth: true,
        body: playerDetailsDraft,
      });
      setMessage("Your tournament gamertag and platform are saved for opponent coordination.");
      await refreshAll();
    } catch (err) {
      setError(err.message || "Could not save your tournament player details.");
    } finally {
      setPlayerDetailsBusy(false);
    }
  }

  const loadMatchLobby = useCallback(async (matchId) => {
    if (!matchId) return;
    setLobbyLoading(true);
    setLobbyError("");
    try {
      const data = await requestJSON(`/tournaments/${TOURNAMENT_ID}/matches/${matchId}/lobby`, {
        auth: true,
      });
      setLobbyData(data || null);
    } catch (err) {
      setLobbyError(err.message || "Could not load the match lobby.");
    } finally {
      setLobbyLoading(false);
    }
  }, []);

  async function openMatchLobby(matchId) {
    setLobbyOpen(true);
    setLobbyMessage("");
    await loadMatchLobby(matchId);
  }

  async function sendLobbyMessage(event) {
    event.preventDefault();
    const matchId = lobbyData?.match?.id;
    const body = String(lobbyMessage || "").trim();
    if (!matchId || !body) return;
    try {
      await requestJSON(`/tournaments/${TOURNAMENT_ID}/matches/${matchId}/lobby/messages`, {
        method: "POST",
        auth: true,
        body: { message: body },
      });
      setLobbyMessage("");
      await loadMatchLobby(matchId);
    } catch (err) {
      setLobbyError(err.message || "Could not send this match lobby message.");
    }
  }

  async function startPayoutSetup(options = {}) {
    const flow = String(options?.flow || "general").toLowerCase();
    const teamId = String(options?.teamId || "");
    if (flow === "captain" && options?.preserveDraft) saveCaptainRegistrationDraft();
    if (flow === "invite" && teamId) saveInviteFlow(teamId);

    const query = new URLSearchParams({ connect: "1", flow });
    if (teamId) query.set("teamId", teamId);
    const anchor = flow === "captain" ? "captain-registration" : flow === "invite" ? "team-invitations" : "team-eligibility";
    const returnPath = `/tournaments?${query.toString()}#${anchor}`;

    setPayoutBusy(true);
    setError("");
    try {
      const data = await requestJSON(`/connect/onboard`, {
        method: "POST",
        auth: true,
        body: { returnPath },
      });
      if (!data?.url) throw new Error("Payout setup link was not returned.");
      await openExternalUrl(data.url);
    } catch (err) {
      setError(err.message || "Could not open payout setup.");
      setPayoutBusy(false);
    }
  }

  async function refreshPayoutStatus() {
    setPayoutBusy(true);
    setError("");
    setPayoutStatusMessage("");
    try {
      const status = await requestJSON(`/connect/status`, { auth: true });
      await requestJSON(`/tournaments/${TOURNAMENT_ID}/payout-eligibility/refresh`, { method: "POST", auth: true });
      await loadMine();
      setPayoutStatusMessage(
        status?.onboardingComplete && status?.payoutsEnabled
          ? "Payout setup confirmed. You are tournament payout-ready."
          : "Payout setup is not complete yet. Continue setup before your team can be approved for prize tournaments."
      );
    } catch (err) {
      setError(err.message || "Could not refresh payout setup status.");
    } finally {
      setPayoutBusy(false);
    }
  }

  async function openPayoutDashboard() {
    setPayoutBusy(true);
    setError("");
    try {
      const data = await requestJSON(`/connect/dashboard-link`, { method: "POST", auth: true });
      if (!data?.url) throw new Error("Payout dashboard link was not returned.");
      await openExternalUrl(data.url);
    } catch (err) {
      setError(err.message || "Could not open your Stripe payout dashboard.");
      setPayoutBusy(false);
    }
  }

  async function cancelTeam(teamId) {
    const backingOut = myTeam?.id === teamId && ["approved", "waitlisted"].includes(myTeam.status);
    const approvedWithdrawal = backingOut && myTeam?.status === "approved";
    const deadlineText = tournament?.withdrawalDeadlineAt
      ? formatTournamentDate(tournament.withdrawalDeadlineAt)
      : "48 hours before the announced tournament start time";
    const confirmed = window.confirm(
      backingOut
        ? approvedWithdrawal
          ? `Back out of the tournament? Your team will lose its approved spot and be removed from the bracket. The withdrawal deadline is ${deadlineText}. If a waitlisted team is ready, it may be promoted into the open slot.`
          : "Leave the tournament waitlist? Your teammates will be released and the team name will become available again."
        : "Cancel this tournament team? Your teammates will be released and the team name will become available again."
    );
    if (!confirmed) return;

    setActionBusy(true);
    setError("");
    setMessage("");
    try {
      const data = await requestJSON(`/tournaments/${TOURNAMENT_ID}/teams/${teamId}/cancel`, {
        method: "POST",
        auth: true,
      });
      setMessage(
        data?.promotedTeamName
          ? `Your team backed out. ${data.promotedTeamName} was promoted from the waitlist into the open slot.`
          : data?.registrationReopened
          ? "Your team backed out. The open tournament slot is available and registration has reopened."
          : backingOut
          ? "Your team has backed out of the tournament."
          : "Team registration cancelled."
      );
      await refreshAll();
    } catch (err) {
      setError(err.message || "Could not remove the team from the tournament.");
    } finally {
      setActionBusy(false);
    }
  }

  async function saveTournamentSchedule({ clear = false } = {}) {
    setActionBusy(true);
    setError("");
    setMessage("");
    try {
      const scheduledAt = clear || !scheduleDraft ? null : new Date(scheduleDraft).toISOString();
      const data = await requestJSON(`/tournaments/${TOURNAMENT_ID}/admin/schedule`, {
        method: "POST",
        auth: true,
        body: { scheduledAt },
      });
      setMessage(
        scheduledAt
          ? `Tournament date saved. Captains may withdraw until ${formatTournamentDate(data?.tournament?.withdrawalDeadlineAt)}.`
          : "Tournament date cleared. The event remains To Be Announced and the 48-hour withdrawal countdown has not started."
      );
      await refreshAll();
    } catch (err) {
      setError(err.message || "Could not update the tournament date.");
    } finally {
      setActionBusy(false);
    }
  }


  async function toggleRegistration(open) {
    setActionBusy(true);
    setError("");
    setMessage("");
    try {
      await requestJSON(`/tournaments/${TOURNAMENT_ID}/admin/registration`, {
        method: "POST",
        auth: true,
        body: { open },
      });
      setMessage(open ? "Tournament registration is now open." : "Tournament registration is now closed.");
      await refreshAll();
    } catch (err) {
      setError(err.message || "Could not update registration status.");
    } finally {
      setActionBusy(false);
    }
  }

  async function sendParticipantBroadcast(type) {
    const preview = PARTICIPANT_EMAIL_PREVIEWS[type];
    const participantCount = Number(myTournament?.adminCommunications?.participantCount || 0);
    if (!preview) return;

    const confirmed = window.confirm(
      `Send the ${preview.label.toLowerCase()} email to ${participantCount} approved real participant${participantCount === 1 ? "" : "s"}? Test bot teams are excluded and duplicate blasts are blocked.`
    );
    if (!confirmed) return;

    setParticipantEmailBusy(type);
    setError("");
    setMessage("");
    try {
      const data = await requestJSON(`/tournaments/${TOURNAMENT_ID}/admin/communications/send`, {
        method: "POST",
        auth: true,
        body: { type },
      });
      setMessage(data?.message || `${preview.label} email sent to approved tournament participants.`);
      await refreshAll();
    } catch (err) {
      setError(err.message || "Could not send the tournament participant email.");
    } finally {
      setParticipantEmailBusy("");
    }
  }

  async function adminTeamDecision(teamId, decision) {
    setActionBusy(true);
    setError("");
    setMessage("");
    try {
      const data = await requestJSON(`/tournaments/${TOURNAMENT_ID}/admin/teams/${teamId}/decision`, {
        method: "POST",
        auth: true,
        body: { decision },
      });
      const newStatus = data?.team?.status;
      setMessage(
        newStatus === "approved"
          ? "Team approved for the tournament."
          : newStatus === "waitlisted"
          ? "Team moved to the waitlist."
          : "Team registration rejected."
      );
      await refreshAll();
    } catch (err) {
      setError(err.message || "Could not update this team.");
    } finally {
      setActionBusy(false);
    }
  }

  useEffect(() => {
    const next = {};
    (Array.isArray(tournament?.matches) ? tournament.matches : []).forEach((match) => {
      next[match.id] = {
        scoreA: Number(match?.scoreA || 0),
        scoreB: Number(match?.scoreB || 0),
      };
    });
    setScoreDrafts(next);
  }, [tournament?.matches]);

  async function startTournament() {
    const confirmed = window.confirm(
      "Start the tournament now? This closes registration, locks captain backout, and enables live series scoring."
    );
    if (!confirmed) return;
    setActionBusy(true);
    setError("");
    setMessage("");
    try {
      await requestJSON(`/tournaments/${TOURNAMENT_ID}/admin/start`, {
        method: "POST",
        auth: true,
      });
      setMessage("Tournament started. Live best-of-3 scoring is now enabled.");
      await refreshAll();
    } catch (err) {
      setError(err.message || "Could not start the tournament.");
    } finally {
      setActionBusy(false);
    }
  }

  async function simulatorAction(action) {
    const confirmations = {
      fill: "Fill every remaining bracket spot with clearly marked test bot teams? Your current teams are preserved in a reset baseline.",
      "run-full": "Simulate the rest of the entire tournament to a champion? This is TEST MODE only and can be reset afterward.",
      reset: "Reset TEST MODE and restore the exact team field and tournament state from before the bots were added?",
    };

    if (confirmations[action] && !window.confirm(confirmations[action])) return;

    setActionBusy(true);
    setError("");
    setMessage("");
    try {
      await requestJSON(`/tournaments/${TOURNAMENT_ID}/admin/simulator/${action}`, {
        method: "POST",
        auth: true,
      });
      const messages = {
        fill: "TEST MODE enabled. Bot teams filled the remaining bracket spots; your pre-test tournament state is protected for reset.",
        "report-next": "A test bot submitted one captain result. If your real team is involved, submit your own final result; matching submissions lock and advance automatically.",
        "confirm-next": "A matching second-captain test submission was added. The result locked and the winner advanced automatically.",
        "dispute-next": "The next pending test result was disputed. Nothing advanced; admin resolution is now required.",
        "resolve-next": "The first disputed test result was resolved and the bracket advanced.",
        "run-full": "The simulator ran the remaining bracket through the real report/confirmation engine to a champion.",
        reset: "TEST MODE reset complete. Your exact pre-test tournament state has been restored.",
      };
      setMessage(messages[action] || "Tournament simulator updated.");
      await refreshAll();
    } catch (err) {
      setError(err.message || "Could not run this tournament simulator action.");
    } finally {
      setActionBusy(false);
    }
  }

  function updateScoreDraft(matchId, side, value) {
    const clean = Math.max(0, Math.min(2, Number(value) || 0));
    setScoreDrafts((current) => ({
      ...current,
      [matchId]: {
        scoreA: Number(current?.[matchId]?.scoreA || 0),
        scoreB: Number(current?.[matchId]?.scoreB || 0),
        [side]: clean,
      },
    }));
  }

  async function saveMatchScore(matchId) {
    const draft = scoreDrafts?.[matchId] || { scoreA: 0, scoreB: 0 };
    setActionBusy(true);
    setError("");
    setMessage("");
    try {
      await requestJSON(`/tournaments/${TOURNAMENT_ID}/admin/matches/${matchId}/score`, {
        method: "POST",
        auth: true,
        body: {
          scoreA: Number(draft.scoreA || 0),
          scoreB: Number(draft.scoreB || 0),
        },
      });
      setMessage("Series score updated. The live bracket has been refreshed.");
      await refreshAll();
    } catch (err) {
      setError(err.message || "Could not update this series score.");
    } finally {
      setActionBusy(false);
    }
  }

  function updateCaptainScoreDraft(side, value) {
    const clean = Math.max(0, Math.min(2, Number(value) || 0));
    setCaptainScoreDraft((current) => ({
      ...current,
      [side]: clean,
    }));
  }

  async function submitCaptainMatchReport(matchId) {
    const scoreA = Number(captainScoreDraft.scoreA || 0);
    const scoreB = Number(captainScoreDraft.scoreB || 0);
    const validLiveScore =
      Number.isInteger(scoreA) &&
      Number.isInteger(scoreB) &&
      scoreA >= 0 &&
      scoreB >= 0 &&
      scoreA <= 2 &&
      scoreB <= 2 &&
      !(scoreA === 2 && scoreB === 2) &&
      scoreA + scoreB > 0;

    if (!validLiveScore) {
      const feedback = "Enter the live series score after at least one game. Valid best-of-3 scores run from 1–0 through 2–1.";
      setCaptainReportFeedback(feedback);
      setError(feedback);
      return;
    }

    const finalScore =
      (scoreA === 2 && scoreB < 2) ||
      (scoreB === 2 && scoreA < 2);

    const confirmed = window.confirm(
      `Update the live score to ${captainMatch?.teamA?.name || "Team A"} ${scoreA}–${scoreB} ${captainMatch?.teamB?.name || "Team B"}? The bracket updates in real time. ${finalScore ? "The winner only advances after both captains submit the same final result." : "You can update it again after the next game."}`
    );
    if (!confirmed) return;

    setActionBusy(true);
    setError("");
    setMessage("");
    setCaptainReportFeedback("");
    try {
      const result = await requestJSON(`/tournaments/${TOURNAMENT_ID}/matches/${matchId}/report`, {
        method: "POST",
        auth: true,
        body: { scoreA, scoreB },
      });

      const outcome = String(result?.outcome || "").toLowerCase();
      if (outcome === "confirmed") {
        setCaptainReportFeedback("Both captains submitted the same final score. The series is locked and the winner advanced automatically.");
        setMessage("Series confirmed and locked. The live bracket advanced the winner automatically.");
      } else if (outcome === "live_confirmed") {
        setCaptainReportFeedback(`Both captains currently agree at ${scoreA}–${scoreB}. The live bracket is updated and the series remains open for the next game.`);
        setMessage("Live score confirmed by both captains. The series remains active.");
      } else if (outcome === "disputed") {
        setCaptainReportFeedback("The captain submissions conflict at the same stage of the series. This matchup is locked for admin review and nothing advances yet.");
        setMessage("Captain results conflict. The series is locked for admin review.");
      } else {
        setCaptainReportFeedback(`Your live score update is saved at ${scoreA}–${scoreB}. The bracket has updated and the opposing captain can submit their current score.`);
        setMessage("Live series score updated.");
      }
      await refreshAll();
    } catch (err) {
      const feedback = err.message || "Could not update this live series score.";
      setCaptainReportFeedback(feedback);
      setError(feedback);
    } finally {
      setActionBusy(false);
    }
  }

  async function submitMatchDispute(event) {
    event.preventDefault();
    if (!captainMatch?.id) return;

    const explanation = String(disputeForm.explanation || "").trim();
    if (explanation.length < 10) {
      setError("Please give a brief explanation of at least 10 characters.");
      return;
    }

    const confirmed = window.confirm(
      "Submit this dispute for admin review? The match will pause until an admin reviews the issue."
    );
    if (!confirmed) return;

    setDisputeSubmitting(true);
    setError("");
    setMessage("");
    try {
      const formData = new FormData();
      formData.append("reasonType", disputeForm.reasonType);
      formData.append("explanation", explanation);
      disputeFiles.slice(0, 3).forEach((file) => formData.append("screenshots", file));

      const response = await fetch(
        apiUrl(`/tournaments/${TOURNAMENT_ID}/matches/${captainMatch.id}/disputes`),
        {
          method: "POST",
          headers: { Authorization: getAuthHeader() },
          body: formData,
          mode: "cors",
          credentials: "omit",
        }
      );

      const text = await response.text();
      let data = null;
      try {
        data = text ? JSON.parse(text) : null;
      } catch {
        data = null;
      }

      if (!response.ok) {
        throw new Error(data?.message || data?.error || `Request failed (${response.status})`);
      }

      setMessage("Dispute submitted. The match is paused for admin review and tournament admins were notified.");
      setDisputeOpen(false);
      setDisputeFiles([]);
      setDisputeForm({ reasonType: "incorrect_score", explanation: "" });
      await refreshAll();
    } catch (err) {
      setError(err.message || "Could not submit this dispute.");
    } finally {
      setDisputeSubmitting(false);
    }
  }

  const registrationOpen = Boolean(tournament?.registrationOpen);
  const fieldFull = Number(tournament?.spotsRemaining || 0) <= 0 && Number(tournament?.approvedTeamCount || 0) >= Number(tournament?.maxTeams || 16);
  const tournamentDateText = formatTournamentDate(tournament?.scheduledAt);
  const withdrawalDeadlineText = tournament?.withdrawalDeadlineAt
    ? formatTournamentDate(tournament.withdrawalDeadlineAt)
    : "48 hours before the announced tournament start time";
  const withdrawalOpen = tournament?.withdrawalOpen !== false;
  const myTeam = myTournament?.team || null;
  const pendingInvites = Array.isArray(myTournament?.pendingInvites)
    ? myTournament.pendingInvites
    : [];
  const isAdmin = Boolean(myTournament?.isAdmin);
  const canManageDisputes = Boolean(myTournament?.canManageDisputes);
  const simulation = myTournament?.simulation || null;
  const testMode = Boolean(tournament?.testMode || simulation?.enabled);
  const currentUserId = String(myTournament?.user?.id || user?.id || "");
  const currentUsername = String(myTournament?.user?.username || user?.username || "").trim().toLowerCase();
  const isMyTeamCaptain = Boolean(
    myTeam &&
      (String(myTeam.captainUserId || "") === currentUserId ||
        (myTeam.members || []).some(
          (member) =>
            member?.role === "captain" &&
            String(member?.username || "").trim().toLowerCase() === currentUsername
        ))
  );
  const adminTeams = Array.isArray(myTournament?.adminTeams) ? myTournament.adminTeams : [];
  const adminCommunications = myTournament?.adminCommunications || null;
  const participantBroadcasts = Array.isArray(adminCommunications?.broadcasts)
    ? adminCommunications.broadcasts
    : [];
  const approvedParticipantCount = Number(adminCommunications?.participantCount || 0);
  const approvedRealTeamCount = Number(adminCommunications?.approvedRealTeamCount || 0);
  const broadcastAlreadySent = (type) =>
    participantBroadcasts.some((broadcast) =>
      String(broadcast?.type || "").toLowerCase() === type &&
      (["sending", "sent", "partial"].includes(String(broadcast?.status || "").toLowerCase()) ||
        Number(broadcast?.deliveredCount || 0) > 0)
    );
  const bracketPlacements = useMemo(() => buildBracketPlacements(tournament?.teams || []), [tournament?.teams]);
  const bracketMatches = Array.isArray(tournament?.matches) ? tournament.matches : [];
  const tournamentLive = ["in_progress", "active", "live"].includes(String(tournament?.status || "").toLowerCase());
  const tournamentComplete = ["completed", "finished"].includes(String(tournament?.status || "").toLowerCase());
  const scoreableMatches = bracketMatches.filter((match) => match?.teamA && match?.teamB);
  const captainMatch = myTournament?.captainMatch || null;
  const myMatch = myTournament?.myMatch || captainMatch || null;
  const myPayout = myTournament?.myPayout || null;
  const myPayoutEligibility = myTournament?.payoutEligibility || null;
  const myPayoutReady = payoutEligibilityReady(myPayoutEligibility);
  const completionSnapshot = tournament?.completionSnapshot || null;
  const myMember = myTeam
    ? (myTeam.members || []).find((member) => String(member?.userId || "") === currentUserId) || null
    : null;
  const needsPlayerDetails = Boolean(
    myMember && (!String(myMember?.gamertag || "").trim() || !String(myMember?.platform || "").trim())
  );
  const captainMatchReport = myTournament?.captainMatchReport || null;
  const latestMatchReport = myTournament?.latestMatchReport || null;
  const adminMatchReports = Array.isArray(myTournament?.adminMatchReports)
    ? myTournament.adminMatchReports
    : [];
  const myDisputes = Array.isArray(myTournament?.myDisputes) ? myTournament.myDisputes : [];
  const myOpenDispute = captainMatch
    ? myDisputes.find(
        (dispute) =>
          dispute?.matchId === captainMatch.id &&
          ["open", "under_review"].includes(String(dispute?.status || "").toLowerCase())
      ) || null
    : null;
  const adminDisputeCount = Number(myTournament?.adminDisputeCount || 0);
  const captainSubmissions = Array.isArray(captainMatchReport?.captainSubmissions)
    ? captainMatchReport.captainSubmissions
    : [];
  const myCaptainSubmission = myTeam
    ? captainSubmissions.find((submission) => submission?.teamId === myTeam.id) || null
    : null;
  const captainReportSubmittedByMyTeam = Boolean(
    myCaptainSubmission ||
      (captainMatchReport && myTeam && captainMatchReport.submittedByTeamId === myTeam.id)
  );
  const captainReportLocked = Boolean(
    captainMatchReport &&
      ["confirmed", "admin_resolved", "disputed"].includes(String(captainMatchReport.status || "").toLowerCase())
  );
  const captainCanSubmit = Boolean(
    captainMatch &&
      !captainReportLocked
  );

  useEffect(() => {
    if (!captainMatch) {
      setCaptainScoreDraft({ scoreA: 0, scoreB: 0 });
      setCaptainReportFeedback("");
      return;
    }
    setCaptainScoreDraft({
      scoreA: Number(captainMatch?.scoreA ?? myCaptainSubmission?.scoreA ?? 0),
      scoreB: Number(captainMatch?.scoreB ?? myCaptainSubmission?.scoreB ?? 0),
    });
  }, [captainMatch, myCaptainSubmission]);

  useEffect(() => {
    if (!pendingInvites.length || window.location.hash !== "#team-invitations") return;
    const id = window.setTimeout(() => {
      document.getElementById("team-invitations")?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 80);
    return () => window.clearTimeout(id);
  }, [pendingInvites.length]);

  useEffect(() => {
    if (!myMember) return;
    setPlayerDetailsDraft({
      gamertag: String(myMember?.gamertag || ""),
      platform: String(myMember?.platform || myTeam?.platform || ""),
    });
  }, [myMember, myTeam]);

  useEffect(() => {
    if (!lobbyOpen || !lobbyData?.match?.id) return;
    const matchId = lobbyData.match.id;
    const timer = window.setInterval(() => loadMatchLobby(matchId), 5000);
    return () => window.clearInterval(timer);
  }, [lobbyOpen, lobbyData?.match?.id, loadMatchLobby]);

  useEffect(() => {
    const targetId = window.location.hash === "#match-report"
      ? "match-report"
      : window.location.hash === "#admin-live-scoring"
      ? "admin-live-scoring"
      : "";
    if (!targetId) return;
    const id = window.setTimeout(() => {
      document.getElementById(targetId)?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 120);
    return () => window.clearTimeout(id);
  }, [captainMatch?.id, captainMatchReport?.id, isAdmin, tournamentLive]);

  useEffect(() => {
    if (window.location.hash !== "#rules") return;
    setRulesTab("rules");
    setRulesOpen(true);
  }, []);

  useEffect(() => {
    let scrollTimer = 0;

    const scrollToRegistration = () => {
      if (window.location.hash !== "#captain-registration" || loading) return;
      window.clearTimeout(scrollTimer);
      scrollTimer = window.setTimeout(() => {
        document.getElementById("captain-registration")?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 180);
    };

    scrollToRegistration();
    window.addEventListener("hashchange", scrollToRegistration);

    return () => {
      window.removeEventListener("hashchange", scrollToRegistration);
      window.clearTimeout(scrollTimer);
    };
  }, [loading, registrationOpen, signedIn]);


  return (
    <div className="page" style={styles.page}>
      <section style={styles.hero}>
        <div style={styles.kicker}>GAStreaming Tournament Hub</div>
        <h1 className="metallic-title" style={styles.title}>Get Active Tournament</h1>
        <p style={styles.subtitle}>
          Free entry. Sixteen teams. 3v3 Park. Max 75 OVR. Best-of-3 series every round.
        </p>
      </section>

      <section style={styles.eventCard}>
        <div style={styles.imageWrap}>
          <img
            src="/get-active-3v3-bracket.jpg"
            alt="GET ACTIVE 3v3 Park Tournament Bracket - Max 75 OVR • $300 Prize"
            style={styles.image}
          />

          <BracketOverlay matches={bracketMatches} fallbackPlacements={bracketPlacements} />

          {tournamentComplete && completionSnapshot && !championOverlayDismissed ? (
            <div id="champion" style={styles.championOverlayBackdrop}>
              <div style={styles.championOverlayCard}>
                <button
                  type="button"
                  aria-label="Close champion overlay"
                  style={styles.championOverlayClose}
                  onClick={() => setChampionOverlayDismissed(true)}
                >
                  ×
                </button>
                <div style={styles.championCrown}>🏆</div>
                <div style={styles.sectionEyebrow}>{completionSnapshot.simulated ? "ADMIN TEST CHAMPION" : "TOURNAMENT CHAMPIONS"}</div>
                <h2 style={styles.championTeamName}>{completionSnapshot.teamName}</h2>
                <div style={styles.championFinalLine}>
                  Final: {completionSnapshot.finalScoreA}–{completionSnapshot.finalScoreB}
                  {completionSnapshot.runnerUpTeamName ? ` vs ${completionSnapshot.runnerUpTeamName}` : ""}
                </div>
                <div style={styles.championRosterLine}>
                  {(completionSnapshot.members || []).map((member) => member.username).filter(Boolean).join(" • ")}
                </div>
                <div style={styles.championPrizeLine}>
                  ${(Number(completionSnapshot.prizeCents || 0) / 100).toFixed(0)} team prize
                  {!completionSnapshot.simulated ? ` • $${(Number(completionSnapshot.prizePerPlayerCents || 0) / 100).toFixed(0)} per winner` : ""}
                </div>
              </div>
            </div>
          ) : null}
        </div>

        <div style={styles.content}>
          <div style={styles.statusRow}>
            <div style={styles.statusPill}>
              {tournamentComplete
                ? "TOURNAMENT COMPLETE"
                : tournamentLive
                ? "TOURNAMENT LIVE"
                : registrationOpen
                ? "REGISTRATION OPEN"
                : fieldFull
                ? "FIELD FULL"
                : "REGISTRATION CLOSED"}
            </div>
            {testMode ? <div style={styles.testModeBadge}>ADMIN TEST MODE</div> : null}
            {tournament ? (
              <div style={styles.spotCounter}>
                {tournament.approvedTeamCount || 0} / {tournament.maxTeams || 16} approved teams
              </div>
            ) : null}
          </div>

          {tournamentComplete && !testMode ? (
            <div style={styles.completionLockNotice}>
              <strong>FINAL RESULT LOCKED.</strong> Registration is permanently closed. No new teams, invite responses, score changes, or tournament re-entry are accepted after completion. The final bracket and champion result remain available as tournament history.
            </div>
          ) : null}

          <h2 style={styles.eventTitle}>3v3 Park • Max 75 OVR</h2>
          <div style={styles.prize}>$300 Winner Prize • $100 Per Winning Player</div>

          <div style={styles.detailsGrid}>
            <Detail label="Entry" value="Free" />
            <Detail label="Teams" value="16 total" />
            <Detail label="Tournament Date" value={tournamentDateText} />
            <Detail label="Registration" value="Closes when 16 teams are approved" />
            <Detail label="Conference Style" value="8 East • 8 West" />
            <Detail label="Series" value="Best 2 out of 3" />
            <Detail label="Roster" value="3 players per team" />
            <Detail label="Platform" value="Crossplay enabled" />
            <Detail label="Account" value="Verified GAStreaming account required" />
            <Detail label="Payout Eligibility" value="Stripe Connect during registration" />
          </div>

          <div style={styles.notice}>
            The captain creates the team and invites two existing GAStreaming users by username. Both teammates must accept before the roster can move to tournament approval. <strong>Crossplay is enabled</strong>. Every player provides a gamertag and platform, which are shared only with current opponents inside a private match lobby so teams can coordinate Park invites and series timing. <strong>Every player must complete GAStreaming Stripe Connect payout onboarding before the team can be approved for prize tournament play.</strong>
          </div>

          <div style={styles.registrationPolicyNotice}>
            <strong>Registration closes automatically once all 16 approved team slots are filled.</strong> Captains may back out until 48 hours before the announced tournament start time so GAStreaming has time to fill the open slot. After that deadline, approved teams are locked in. A team that cannot play or fails to appear may forfeit its matchup, allowing the opponent to advance. <strong>Tournament date: {tournamentDateText}.</strong>
          </div>

          <button
            id="rules"
            type="button"
            style={styles.rulesHelpLink}
            onClick={() => {
              setRulesTab("rules");
              setRulesOpen(true);
            }}
          >
            ⓘ Rules & How the Tournament Works
          </button>



          {tournamentComplete && myPayout && !myPayout.simulated ? (
            <div id="payout" style={styles.winnerPayoutCard}>
              <div style={{ flex: "1 1 520px" }}>
                <div style={styles.sectionEyebrow}>YOUR INDIVIDUAL TOURNAMENT PRIZE</div>
                <div style={styles.winnerPayoutAmount}>${(Number(myPayout.amountCents || 0) / 100).toFixed(2)}</div>
                <div style={styles.winnerPayoutCopy}>
                  {myPayout.status === "paid"
                    ? "Paid through Stripe Connect. Your championship is also saved permanently in your profile achievements."
                    : myPayout.status === "processing"
                    ? "Your payout is currently being processed. Do not create another payout account or submit duplicate payout requests."
                    : myPayout.status === "failed"
                    ? `The last payout attempt needs attention${myPayout.failureMessage ? `: ${myPayout.failureMessage}` : "."}`
                    : myPayout?.connection?.onboardingComplete && myPayout?.connection?.payoutsEnabled
                    ? "Your secure Stripe Connect payout account is ready. GAStreaming admin can send your individual prize independently from your teammates' payouts."
                    : "Complete secure Stripe Connect payout setup so GAStreaming can send your individual prize directly to you. Your teammates complete their own setup separately."}
                </div>
                {payoutStatusMessage ? <div style={styles.payoutStatusMessage}>{payoutStatusMessage}</div> : null}
              </div>
              <div style={styles.payoutActionRow}>
                {myPayout.status !== "paid" && !(myPayout?.connection?.onboardingComplete && myPayout?.connection?.payoutsEnabled) ? (
                  <>
                    <button type="button" style={styles.smallPrimaryButton} disabled={payoutBusy} onClick={startPayoutSetup}>
                      {payoutBusy ? "Opening…" : "Set Up Tournament Payout"}
                    </button>
                    <button type="button" style={styles.secondaryActionButton} disabled={payoutBusy} onClick={refreshPayoutStatus}>
                      Refresh Payout Status
                    </button>
                  </>
                ) : (
                  <>
                    <button type="button" style={styles.secondaryActionButton} disabled={payoutBusy} onClick={openPayoutDashboard}>
                      Open Stripe Payout Dashboard
                    </button>
                    <Link to="/me#tournament-achievements" style={styles.adminReviewLink}>View Profile Achievement</Link>
                  </>
                )}
              </div>
            </div>
          ) : null}

          {error ? <div style={styles.errorBox}>{error}</div> : null}
          {message ? <div style={styles.successBox}>{message}</div> : null}

          {loading ? (
            <div style={styles.loadingBox}>Loading tournament registration…</div>
          ) : !signedIn ? (
            <div id="captain-registration" style={styles.registrationBox}>
              <div style={styles.sectionEyebrow}>TEAM REGISTRATION</div>
              <h3 style={styles.sectionTitle}>A free GAStreaming account is required.</h3>
              <p style={styles.sectionCopy}>
                Create an account or sign in first. Captains invite teammates using their GAStreaming usernames.
              </p>
              <Link to="/register" style={styles.cta}>Create Free Account to Enter</Link>
            </div>
          ) : (
            <>
              {pendingInvites.length > 0 ? (
                <section id="team-invitations" style={styles.registrationBox}>
                  <div style={styles.sectionEyebrow}>TEAM INVITATIONS</div>
                  <h3 style={styles.sectionTitle}>You have a tournament invite.</h3>
                  <p style={styles.sectionCopy}>
                    Accept the invite to begin a guided qualification flow. Returning payout-ready players skip Stripe setup automatically. New players complete Stripe Connect first, then add their own NBA 2K gamertag and platform before the roster spot is confirmed.
                  </p>
                  <div style={styles.inviteList}>
                    {pendingInvites.map(({ team }) => {
                      const details = inviteDetails?.[team.id] || {};
                      const inviteFlowActive = activeInviteFlowTeamId === team.id;
                      return (
                        <div key={team.id} style={styles.inviteCard}>
                          <div style={{ flex: "1 1 100%" }}>
                            <div style={styles.inviteTeamName}>{team.name}</div>
                            <div style={styles.inviteMeta}>
                              Captain: {team.members?.find((member) => member.role === "captain")?.username || "Captain"}
                            </div>
                          </div>

                          <div style={styles.registrationFlowSteps}>
                            <FlowStep number="1" title="Accept the invitation" copy="Choose to continue with this team. Declining ends this invite immediately." />
                            <FlowStep number="2" title="Verify payout eligibility" copy="Already payout-ready players skip ahead. Everyone else completes GAStreaming Stripe Connect onboarding." />
                            <FlowStep number="3" title="Add your player details" copy="Enter your own NBA 2K gamertag and platform for private opponent coordination." />
                            <FlowStep number="4" title="Confirm the roster spot" copy="Your spot becomes confirmed only after payout eligibility and player details are complete." />
                          </div>

                          {!inviteFlowActive ? (
                            <div style={styles.actionRow}>
                              <button
                                type="button"
                                style={styles.smallPrimaryButton}
                                disabled={actionBusy || payoutBusy}
                                onClick={() => beginInviteAcceptance(team.id)}
                              >
                                {myPayoutReady ? "Accept Invite & Continue" : "Accept Invite & Start Payout Setup"}
                              </button>
                              <button
                                type="button"
                                style={styles.smallGhostButton}
                                disabled={actionBusy}
                                onClick={() => respondToInvite(team.id, "decline")}
                              >
                                Decline
                              </button>
                            </div>
                          ) : (
                            <div id={`invite-details-${team.id}`} style={styles.inviteFlowPanel}>
                              <div style={myPayoutReady ? styles.flowStatusReady : styles.flowStatusNeeded}>
                                <div style={styles.flowStatusLabel}>STEP 2 — PAYOUT ELIGIBILITY</div>
                                <div style={styles.flowStatusTitle}>{payoutEligibilityLabel(myPayoutEligibility)}</div>
                                <div style={styles.flowStatusCopy}>
                                  {myPayoutReady
                                    ? "Your existing GAStreaming Stripe Connect payout setup is ready, so you can continue without repeating onboarding."
                                    : "Complete Stripe Connect payout onboarding before this prize-tournament roster spot can be confirmed."}
                                </div>
                                {payoutStatusMessage ? <div style={styles.payoutStatusMessage}>{payoutStatusMessage}</div> : null}
                              </div>

                              {!myPayoutReady ? (
                                <div style={styles.actionRow}>
                                  <button
                                    type="button"
                                    style={styles.smallPrimaryButton}
                                    disabled={payoutBusy}
                                    onClick={() => startPayoutSetup({ flow: "invite", teamId: team.id })}
                                  >
                                    {payoutBusy ? "Opening Stripe…" : "Continue Stripe Payout Setup"}
                                  </button>
                                  <button
                                    type="button"
                                    style={styles.secondaryActionButton}
                                    disabled={payoutBusy}
                                    onClick={refreshPayoutStatus}
                                  >
                                    Refresh Eligibility
                                  </button>
                                </div>
                              ) : (
                                <>
                                  <div style={styles.flowSectionHeader}>
                                    <div style={styles.flowStepBadge}>3</div>
                                    <div>
                                      <div style={styles.flowSectionTitle}>Add your NBA 2K gamertag and platform</div>
                                      <div style={styles.gamertagDisclosure}>
                                        These details are shared only with your current tournament opponents inside the private match lobby so teams can send Park invites and coordinate the series.
                                      </div>
                                    </div>
                                  </div>
                                  <div style={styles.inviteDetailsGrid}>
                                    <input
                                      type="text"
                                      maxLength={32}
                                      value={details.gamertag || ""}
                                      onChange={(event) => updateInviteDetails(team.id, "gamertag", event.target.value)}
                                      style={styles.input}
                                      placeholder="Your NBA 2K gamertag"
                                    />
                                    <input
                                      type="text"
                                      maxLength={40}
                                      value={details.platform || ""}
                                      onChange={(event) => updateInviteDetails(team.id, "platform", event.target.value)}
                                      style={styles.input}
                                      placeholder="Your platform, e.g. PS5"
                                    />
                                  </div>
                                  <div style={styles.actionRow}>
                                    <button
                                      type="button"
                                      style={styles.smallPrimaryButton}
                                      disabled={actionBusy || !String(details.gamertag || "").trim() || !String(details.platform || "").trim()}
                                      onClick={() => respondToInvite(team.id, "accept")}
                                    >
                                      {actionBusy ? "Confirming…" : "Confirm Roster Spot"}
                                    </button>
                                    <button
                                      type="button"
                                      style={styles.smallGhostButton}
                                      disabled={actionBusy}
                                      onClick={() => {
                                        setActiveInviteFlowTeamId("");
                                        clearSessionKey(INVITE_ACCEPTANCE_FLOW_KEY);
                                      }}
                                    >
                                      Finish Later
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </section>
              ) : null}


              {myTeam ? (
                <section style={styles.registrationBox}>
                  <div style={styles.sectionEyebrow}>YOUR TEAM</div>
                  <div style={styles.teamHeadingRow}>
                    <div>
                      <h3 style={styles.sectionTitle}>{myTeam.name}</h3>
                      <p style={styles.sectionCopy}>{statusLabel(myTeam.status)}</p>
                    </div>
                    <div style={styles.teamStatusBadge}>{statusLabel(myTeam.status)}</div>
                  </div>

                  {myTeam?.payoutReadiness && !myTeam.payoutReadiness.ready && !["approved", "waitlisted", "eliminated", "champion"].includes(myTeam.status) ? (
                    <div style={styles.eligibilityWarning}>
                      Team not fully qualified yet: {myTeam.payoutReadiness.readyCount || 0}/3 players are Stripe payout-ready. All three players must be payout-ready before admin approval.
                    </div>
                  ) : null}

                  {isMyTeamCaptain && !["eliminated", "champion", "withdrawn", "cancelled"].includes(myTeam.status) ? (
                    <div style={styles.captainActions}>
                      {myTeam.status === "approved" && !withdrawalOpen ? (
                        <button type="button" style={{ ...styles.cancelButton, ...styles.ctaDisabled }} disabled>
                          Withdrawal Deadline Passed
                        </button>
                      ) : (
                        <button
                          type="button"
                          style={styles.cancelButton}
                          disabled={actionBusy}
                          onClick={() => cancelTeam(myTeam.id)}
                        >
                          {["approved", "waitlisted"].includes(myTeam.status)
                            ? myTeam.status === "waitlisted"
                              ? "Leave Tournament Waitlist"
                              : "Back Out of Tournament"
                            : "Cancel Team Registration"}
                        </button>
                      )}
                      <div style={styles.captainActionHint}>
                        {myTeam.status === "approved"
                          ? withdrawalOpen
                            ? tournament?.withdrawalDeadlineAt
                              ? `Available until ${withdrawalDeadlineText}. Backing out before the deadline frees the slot for a waitlisted or replacement team.`
                              : "Available until 48 hours before the tournament start time once the date is announced. Because the date is still TBD, the withdrawal window remains open."
                            : "The 48-hour withdrawal deadline has passed. Your approved team is locked in; a no-show or inability to play may result in a forfeit and opponent advancement."
                          : myTeam.status === "waitlisted"
                          ? "Leaving the waitlist releases the roster and team name."
                          : "Cancelling releases the roster and makes the team name available again."}
                      </div>
                    </div>
                  ) : null}

                  <div style={styles.rosterGrid}>
                    {(myTeam.members || []).map((member) => (
                      <div key={member.userId} style={styles.rosterCard}>
                        <div style={styles.rosterName}>{member.username}</div>
                        <div style={styles.rosterRole}>{member.role === "captain" ? "Captain" : "Player"}</div>
                        <div style={member.status === "accepted" ? styles.confirmedText : member.status === "declined" ? styles.declinedText : styles.pendingText}>
                          {memberStatusLabel(member.status)}
                        </div>
                        {member.status === "accepted" && (member.gamertag || member.platform) ? (
                          <div style={styles.rosterGameDetails}>
                            {member.gamertag ? `🎮 ${member.gamertag}` : "Gamertag pending"}
                            {member.platform ? ` • ${member.platform}` : ""}
                          </div>
                        ) : null}
                        {member.status === "accepted" ? (
                          <div style={payoutEligibilityReady(member.payoutEligibility) ? styles.payoutReadyText : styles.payoutNeededText}>
                            {payoutEligibilityReady(member.payoutEligibility) ? "✅ " : "⏳ "}{payoutEligibilityLabel(member.payoutEligibility)}
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>

                  {needsPlayerDetails ? (
                    <div style={styles.playerDetailsPrompt}>
                      <div>
                        <div style={styles.playerDetailsTitle}>Add your Park invite details</div>
                        <div style={styles.playerDetailsCopy}>
                          Your gamertag and platform are disclosed only to teams you are actively matched against for tournament coordination.
                        </div>
                      </div>
                      <div style={styles.playerDetailsInputs}>
                        <input
                          type="text"
                          maxLength={32}
                          value={playerDetailsDraft.gamertag}
                          onChange={(event) => setPlayerDetailsDraft((current) => ({ ...current, gamertag: event.target.value }))}
                          style={styles.input}
                          placeholder="NBA 2K gamertag"
                        />
                        <input
                          type="text"
                          maxLength={40}
                          value={playerDetailsDraft.platform}
                          onChange={(event) => setPlayerDetailsDraft((current) => ({ ...current, platform: event.target.value }))}
                          style={styles.input}
                          placeholder="Platform, e.g. PS5"
                        />
                        <button
                          type="button"
                          style={styles.smallPrimaryButton}
                          disabled={playerDetailsBusy}
                          onClick={saveMyPlayerDetails}
                        >
                          {playerDetailsBusy ? "Saving…" : "Save Player Details"}
                        </button>
                      </div>
                    </div>
                  ) : null}

                  {myMatch?.id && myMatch?.teamA && myMatch?.teamB ? (
                    <button
                      type="button"
                      style={styles.matchLobbyButton}
                      onClick={() => openMatchLobby(myMatch.id)}
                    >
                      💬 Open Match Lobby & Gamertags
                    </button>
                  ) : null}

                  {isMyTeamCaptain && tournamentLive ? (
                    <div id="match-report" style={styles.captainMatchSection}>
                      <div style={styles.sectionEyebrow}>CAPTAIN MATCH REPORTING</div>
                      <h4 style={styles.captainMatchTitle}>Your live best-of-3 series</h4>

                      {!captainMatch ? (
                        <div style={styles.captainMatchStatusBox}>
                          No active matchup is ready for your team right now.
                        </div>
                      ) : captainMatch.status === "waiting" || !captainMatch.teamA || !captainMatch.teamB ? (
                        <div style={styles.captainMatchStatusBox}>
                          Your team has advanced and is waiting for the next opponent to be decided.
                        </div>
                      ) : (
                        <>
                          <div style={styles.captainMatchMeta}>
                            {captainMatch.conference === "final"
                              ? "CHAMPIONSHIP"
                              : `${String(captainMatch.conference || "").toUpperCase()} • ROUND ${captainMatch.round}`}
                          </div>

                          <div style={styles.captainResultGrid}>
                            <div style={styles.captainResultTeamRow}>
                              <span style={styles.captainResultTeamName}>{captainMatch.teamA.name}</span>
                              <input
                                type="number"
                                min="0"
                                max="2"
                                inputMode="numeric"
                                value={captainScoreDraft.scoreA}
                                disabled={actionBusy || !captainCanSubmit}
                                onChange={(event) => updateCaptainScoreDraft("scoreA", event.target.value)}
                                style={styles.captainResultInput}
                                aria-label={`${captainMatch.teamA.name} series wins`}
                              />
                            </div>
                            <div style={styles.captainResultTeamRow}>
                              <span style={styles.captainResultTeamName}>{captainMatch.teamB.name}</span>
                              <input
                                type="number"
                                min="0"
                                max="2"
                                inputMode="numeric"
                                value={captainScoreDraft.scoreB}
                                disabled={actionBusy || !captainCanSubmit}
                                onChange={(event) => updateCaptainScoreDraft("scoreB", event.target.value)}
                                style={styles.captainResultInput}
                                aria-label={`${captainMatch.teamB.name} series wins`}
                              />
                            </div>
                          </div>

                          {captainCanSubmit ? (
                            <>
                              <p style={styles.captainResultHelp}>
                                Update the score after each game if you want, or wait and submit the completed series all at once. The live bracket updates as scores are reported. A matching final 2–0 or 2–1 submission from both captains locks the series and advances the winner.
                              </p>
                              {captainMatchReport?.status === "live_confirmed" ? (
                                <div style={styles.awaitingConfirmationText}>
                                  Both captains currently agree at {captainMatchReport.scoreA}–{captainMatchReport.scoreB}. The series is still open, so update the score again after the next game.
                                </div>
                              ) : captainReportSubmittedByMyTeam ? (
                                <div style={styles.awaitingConfirmationText}>
                                  Your latest score update is saved. You can submit another update after the next game, or wait until the series is complete.
                                </div>
                              ) : captainMatchReport ? (
                                <div style={styles.awaitingConfirmationText}>
                                  The opposing captain has already reported a score. Submit your current score whenever you are ready.
                                </div>
                              ) : null}
                              {captainReportFeedback ? (
                                <div style={styles.captainReportFeedback}>{captainReportFeedback}</div>
                              ) : null}
                              <button
                                type="button"
                                style={styles.submitCaptainResultButton}
                                disabled={actionBusy}
                                onClick={() => submitCaptainMatchReport(captainMatch.id)}
                              >
                                {actionBusy ? "Updating…" : "Update Live Series Score"}
                              </button>

                              {myOpenDispute ? (
                                <div style={styles.matchUnderReviewBanner}>
                                  ⚠️ MATCH UNDER REVIEW — {myOpenDispute.reasonLabel || "Tournament dispute"}. No team advances until admin resolves it.
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  style={styles.disputeMatchButton}
                                  disabled={actionBusy || disputeSubmitting}
                                  onClick={() => setDisputeOpen(true)}
                                >
                                  Dispute Match / Report Violation
                                </button>
                              )}
                            </>
                          ) : captainMatchReport ? (
                            <div style={styles.matchReportStatusCard}>
                              <div style={styles.matchReportStatusTitle}>
                                {matchReportStatusLabel(captainMatchReport.status)}
                              </div>
                              <div style={styles.matchReportStatusCopy}>
                                {captainMatchReport.status === "confirmed"
                                  ? `Both captains submitted matching final results: ${captainMatch.teamA.name} ${captainMatchReport.scoreA}–${captainMatchReport.scoreB} ${captainMatch.teamB.name}. This series is locked.`
                                  : captainMatchReport.status === "disputed"
                                  ? "The captain submissions conflict. This series is locked for admin review and nothing advances until it is resolved."
                                  : `Live score: ${captainMatch.teamA.name} ${captainMatchReport.scoreA}–${captainMatchReport.scoreB} ${captainMatch.teamB.name}.`}
                              </div>
                              {captainMatchReport.status === "disputed" && captainMatchReport.disputeReason ? (
                                <div style={styles.disputeReasonText}>Reason: {captainMatchReport.disputeReason}</div>
                              ) : null}
                            </div>
                          ) : null}
                        </>
                      )}

                      {!captainMatchReport && latestMatchReport && ["confirmed", "admin_resolved", "disputed"].includes(latestMatchReport.status) ? (
                        <div style={styles.latestResultStrip}>
                          Latest report: {matchReportStatusLabel(latestMatchReport.status)}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </section>
              ) : registrationOpen ? (
                <section id="captain-registration" style={styles.registrationBox}>
                  <div style={styles.sectionEyebrow}>CREATE YOUR 3V3 TEAM</div>
                  <h3 style={styles.sectionTitle}>Captain creates. Teammates confirm.</h3>
                  <p style={styles.sectionCopy}>
                    Build the roster first. GAStreaming then checks your payout eligibility inside this same registration flow. Returning payout-ready players skip Stripe onboarding automatically. New players complete secure Stripe Connect setup before the team is created and invites are sent.
                  </p>

                  <div style={styles.registrationFlowSteps}>
                    <FlowStep number="1" title="Enter team and player details" copy="Choose the team name, add your own platform and NBA 2K gamertag, then enter two exact GAStreaming teammate usernames." />
                    <FlowStep number="2" title="Complete payout eligibility" copy="If you are already payout-ready, nothing extra is required. Otherwise your team draft is saved while you complete Stripe Connect onboarding." />
                    <FlowStep number="3" title="Create team and send invites" copy="After the captain is payout-ready, the team is created and both teammates receive on-site and email invitations to complete their own qualification flow." />
                  </div>

                  <form onSubmit={createTeam} style={styles.form}>
                    <label style={styles.label}>
                      Team name
                      <input
                        type="text"
                        maxLength={36}
                        required
                        value={form.teamName}
                        onChange={(event) => updateForm("teamName", event.target.value)}
                        style={styles.input}
                        placeholder="Example: Get Active Elite"
                      />
                    </label>

                    <label style={styles.label}>
                      Your platform — Crossplay enabled
                      <input
                        type="text"
                        maxLength={40}
                        required
                        value={form.platform}
                        onChange={(event) => updateForm("platform", event.target.value)}
                        style={styles.input}
                        placeholder="Example: PS5"
                      />
                      <span style={styles.fieldHelp}>
                        This tournament uses crossplay. Your platform is collected only for roster and match coordination.
                      </span>
                    </label>

                    <label style={styles.label}>
                      Your NBA 2K gamertag
                      <input
                        type="text"
                        maxLength={32}
                        required
                        value={form.captainGamertag}
                        onChange={(event) => updateForm("captainGamertag", event.target.value)}
                        style={styles.input}
                        placeholder="Exact gamertag opponents should invite"
                      />
                      <span style={styles.fieldHelp}>
                        Your gamertag is shown only to your active tournament opponents inside the private match lobby.
                      </span>
                    </label>

                    <label style={styles.label}>
                      Teammate 1 GAStreaming username
                      <input
                        type="text"
                        required
                        value={form.teammateOne}
                        onChange={(event) => updateForm("teammateOne", event.target.value)}
                        style={styles.input}
                        placeholder="Exact GAStreaming username"
                      />
                    </label>

                    <label style={styles.label}>
                      Teammate 2 GAStreaming username
                      <input
                        type="text"
                        required
                        value={form.teammateTwo}
                        onChange={(event) => updateForm("teammateTwo", event.target.value)}
                        style={styles.input}
                        placeholder="Exact GAStreaming username"
                      />
                    </label>

                    <div id="team-eligibility" style={myPayoutReady ? styles.registrationEligibilityReady : styles.registrationEligibilityNeeded}>
                      <div style={styles.flowSectionHeader}>
                        <div style={styles.flowStepBadge}>2</div>
                        <div style={{ flex: 1 }}>
                          <div style={styles.flowStatusLabel}>CAPTAIN PAYOUT ELIGIBILITY</div>
                          <div style={styles.flowStatusTitle}>{payoutEligibilityLabel(myPayoutEligibility)}</div>
                          <div style={styles.flowStatusCopy}>
                            {myPayoutReady
                              ? "Your existing GAStreaming Stripe Connect payout account is ready. You will skip onboarding and can create the team immediately."
                              : "Your completed team form will be saved before GAStreaming sends you to Stripe Connect. After you return payout-ready, the saved registration will be restored so you can finish without retyping everything."}
                          </div>
                          {payoutStatusMessage ? <div style={styles.payoutStatusMessage}>{payoutStatusMessage}</div> : null}
                        </div>
                      </div>
                      <div style={styles.payoutActionRow}>
                        {myPayoutReady ? (
                          <>
                            <button type="button" style={styles.secondaryActionButton} disabled={payoutBusy} onClick={openPayoutDashboard}>
                              Open Stripe Dashboard
                            </button>
                            <button type="button" style={styles.secondaryActionButton} disabled={payoutBusy} onClick={refreshPayoutStatus}>
                              Refresh Eligibility
                            </button>
                          </>
                        ) : (
                          <button type="button" style={styles.secondaryActionButton} disabled={payoutBusy} onClick={refreshPayoutStatus}>
                            Refresh Eligibility
                          </button>
                        )}
                      </div>
                    </div>

                    {registrationResumeReady ? (
                      <div style={styles.registrationResumeBanner}>
                        ✅ Stripe payout setup is ready. Your saved registration details were restored — review them and create the team.
                      </div>
                    ) : null}

                    <button type="submit" style={styles.cta} disabled={actionBusy || payoutBusy}>
                      {actionBusy
                        ? "Creating Team…"
                        : payoutBusy
                        ? "Opening Stripe…"
                        : myPayoutReady
                        ? "Create Team & Send Invites"
                        : "Continue to Stripe Payout Setup"}
                    </button>
                    <div style={styles.registrationFinePrint}>
                      Prize-tournament approval requires 3/3 roster confirmations and 3/3 payout-ready players. A GAStreaming admin can host and manage the tournament without joining a roster or qualifying as a participant.
                    </div>
                  </form>
                </section>

              ) : (
                <section style={styles.registrationBox}>
                  <div style={styles.sectionEyebrow}>REGISTRATION STATUS</div>
                  <h3 style={styles.sectionTitle}>
                    {fieldFull ? "Registration is closed — the 16-team field is full." : "Registration is currently closed."}
                  </h3>
                  <p style={styles.sectionCopy}>
                    Registration closes once all 16 approved team slots are filled. Captains may back out until 48 hours before the announced tournament start time so GAStreaming has time to fill an open slot. After that deadline, approved teams are locked in; a team that cannot play or fails to appear may forfeit its matchup and the opponent may advance. Tournament date: <strong>{tournamentDateText}</strong>.
                  </p>
                  <button type="button" style={{ ...styles.cta, ...styles.ctaDisabled }} disabled>
                    {fieldFull ? "Registration Closed — Field Full" : "Registration Currently Closed"}
                  </button>
                </section>
              )}
            </>
          )}


          {canManageDisputes && !isAdmin ? (
            <section style={styles.adminSection}>
              <div style={styles.adminHeader}>
                <div>
                  <div style={styles.sectionEyebrow}>DISPUTE ADMIN</div>
                  <h3 style={styles.sectionTitle}>Tournament dispute reviews</h3>
                  <p style={styles.sectionCopy}>
                    Your staff access is limited to reviewing match disputes, checking submitted proof, resuming a paused match, or applying the official score. Team approvals, tournament setup, payouts, communications, and bot testing remain owner-only.
                  </p>
                </div>
                <div style={styles.adminHeaderActions}>
                  <Link to="/admin/tournament-reviews" style={styles.adminReviewLink}>
                    Open Dispute Center{adminDisputeCount ? ` (${adminDisputeCount})` : ""}
                  </Link>
                </div>
              </div>
            </section>
          ) : null}

          {isAdmin ? (
            <section style={styles.adminSection}>
              <div style={styles.adminHeader}>
                <div>
                  <div style={styles.sectionEyebrow}>TOURNAMENT ADMIN</div>
                  <h3 style={styles.sectionTitle}>Registration & team approval</h3>
                  <p style={styles.sectionCopy}>
                    Open or close registration, review completed rosters, and control the 16-team field. Registration closes automatically when the 16th team is approved. If an approved team withdraws before the 48-hour deadline, the first waitlisted team is promoted or registration reopens for the empty slot.
                  </p>
                </div>
                <div style={styles.adminHeaderActions}>
                  {!tournamentLive && !tournamentComplete && !testMode ? (
                    <button
                      type="button"
                      style={registrationOpen ? styles.adminCloseButton : styles.smallPrimaryButton}
                      disabled={actionBusy}
                      onClick={() => toggleRegistration(!registrationOpen)}
                    >
                      {registrationOpen ? "Close Registration" : "Open Registration"}
                    </button>
                  ) : null}
                  {!tournamentLive && !tournamentComplete ? (
                    <button
                      type="button"
                      style={tournament?.bracketReady ? styles.startTournamentButton : styles.disabledAdminButton}
                      disabled={actionBusy || !tournament?.bracketReady}
                      onClick={startTournament}
                    >
                      {tournament?.bracketReady
                        ? "Start Tournament"
                        : `Start unlocks at ${tournament?.maxTeams || 16}/${tournament?.maxTeams || 16}`}
                    </button>
                  ) : null}
                  <Link to="/admin/tournament-reviews" style={styles.adminReviewLink}>
                    Admin Reviews{adminDisputeCount ? ` (${adminDisputeCount})` : ""}
                  </Link>
                  {tournamentComplete && !testMode ? (
                    <Link to="/admin/tournament-payouts" style={styles.adminReviewLink}>
                      Winner Payouts
                    </Link>
                  ) : null}
                </div>
              </div>

              <div style={styles.adminStats}>
                <Detail label="Approved" value={`${tournament?.approvedTeamCount || 0} / ${tournament?.maxTeams || 16}`} />
                <Detail label="Ready for review" value={String(adminTeams.filter((team) => team.status === "ready_for_review").length)} />
                <Detail label="Waiting on teammates" value={String(adminTeams.filter((team) => team.status === "awaiting_teammates").length)} />
                <Detail label="Waitlisted" value={String(adminTeams.filter((team) => team.status === "waitlisted").length)} />
              </div>

              <div style={styles.schedulePanel}>
                <div style={{ flex: "1 1 420px" }}>
                  <div style={styles.sectionEyebrow}>TOURNAMENT DATE & WITHDRAWAL CUTOFF</div>
                  <h4 style={styles.scheduleTitle}>{tournament?.scheduledAt ? tournamentDateText : "Tournament Date: To Be Announced"}</h4>
                  <p style={styles.scheduleCopy}>
                    The 48-hour captain withdrawal deadline is calculated automatically from the date and time saved here. Until a date is announced, approved captains may still back out and free their slot.
                  </p>
                </div>
                <div style={styles.scheduleControls}>
                  <input
                    type="datetime-local"
                    value={scheduleDraft}
                    onChange={(event) => setScheduleDraft(event.target.value)}
                    style={styles.scheduleInput}
                    disabled={actionBusy || tournamentLive || tournamentComplete || testMode}
                    aria-label="Tournament date and start time"
                  />
                  <button
                    type="button"
                    style={styles.smallPrimaryButton}
                    disabled={actionBusy || !scheduleDraft || tournamentLive || tournamentComplete || testMode}
                    onClick={() => saveTournamentSchedule()}
                  >
                    Save Tournament Date
                  </button>
                  <button
                    type="button"
                    style={styles.secondaryActionButton}
                    disabled={actionBusy || !tournament?.scheduledAt || tournamentLive || tournamentComplete || testMode}
                    onClick={() => saveTournamentSchedule({ clear: true })}
                  >
                    Keep Date TBD
                  </button>
                </div>
              </div>

              <div style={styles.simulatorPanel}>
                <div style={styles.simulatorHeader}>
                  <div>
                    <div style={styles.simulatorEyebrow}>ADMIN TEST LAB</div>
                    <h4 style={styles.simulatorTitle}>Tournament bot simulator</h4>
                    <p style={styles.simulatorCopy}>
                      Admin-only. Fill empty spots with clearly marked bot teams, test the real captain submission → matching second submission or dispute → automatic advancement workflow, and restore your exact pre-test tournament state with Reset Test Mode.
                    </p>
                  </div>
                  <div style={testMode ? styles.testModeActivePill : styles.testModeIdlePill}>
                    {testMode ? "TEST MODE ACTIVE" : "TEST MODE OFF"}
                  </div>
                </div>

                {!testMode ? (
                  <button
                    type="button"
                    style={styles.simulatorPrimaryButton}
                    disabled={actionBusy || tournamentLive || tournamentComplete}
                    onClick={() => simulatorAction("fill")}
                  >
                    Fill Remaining Slots With Test Bots
                  </button>
                ) : (
                  <>
                    <div style={styles.simulatorStats}>
                      <Detail label="Test bots" value={String(simulation?.botTeamCount || tournament?.simulationBotTeamCount || 0)} />
                      <Detail label="Simulated reports" value={String(simulation?.reportCounter || 0)} />
                      <Detail label="Tournament state" value={tournamentComplete ? "Complete" : tournamentLive ? "Live" : "Ready to start"} />
                    </div>

                    <div style={styles.simulatorActions}>
                      {tournamentLive && !tournamentComplete ? (
                        <>
                          <button type="button" style={styles.simulatorPrimaryButton} disabled={actionBusy} onClick={() => simulatorAction("report-next")}>
                            Bot Report Next Match
                          </button>
                          <button type="button" style={styles.simulatorConfirmButton} disabled={actionBusy} onClick={() => simulatorAction("confirm-next")}>
                            Match Next Pending
                          </button>
                          <button type="button" style={styles.simulatorDisputeButton} disabled={actionBusy} onClick={() => simulatorAction("dispute-next")}>
                            Dispute Next Pending
                          </button>
                          <button type="button" style={styles.simulatorResolveButton} disabled={actionBusy} onClick={() => simulatorAction("resolve-next")}>
                            Resolve First Dispute
                          </button>
                          <button type="button" style={styles.simulatorRunButton} disabled={actionBusy} onClick={() => simulatorAction("run-full")}>
                            Simulate Full Tournament
                          </button>
                        </>
                      ) : null}
                      <button type="button" style={styles.simulatorResetButton} disabled={actionBusy} onClick={() => simulatorAction("reset")}>
                        Reset Test Mode
                      </button>
                    </div>

                    {!tournamentLive && !tournamentComplete ? (
                      <div style={styles.simulatorHint}>
                        The bracket is full. Use the existing <strong>Start Tournament</strong> button above to begin the test, then the bot workflow controls will unlock.
                      </div>
                    ) : null}

                    {Array.isArray(simulation?.log) && simulation.log.length ? (
                      <div style={styles.simulatorLog}>
                        <div style={styles.simulatorLogTitle}>Recent test activity</div>
                        {simulation.log.slice().reverse().slice(0, 8).map((entry) => (
                          <div key={entry.id || `${entry.createdAt}-${entry.action}`} style={styles.simulatorLogRow}>
                            <span>{entry.message}</span>
                            <span style={styles.simulatorLogTime}>
                              {entry.createdAt ? new Date(entry.createdAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : ""}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </>
                )}
              </div>

              <div style={styles.communicationsPanel}>
                <div style={styles.communicationsHeader}>
                  <div>
                    <div style={styles.sectionEyebrow}>PARTICIPANT COMMUNICATIONS</div>
                    <h4 style={styles.communicationsTitle}>Keep every approved player on the same page.</h4>
                    <p style={styles.sectionCopy}>
                      Manual foundation for tournament-wide emails. Approved real participants are pulled from their GAStreaming account emails; duplicate recipients and test bot teams are excluded.
                    </p>
                  </div>
                  <div style={styles.communicationsCount}>
                    <strong>{approvedParticipantCount}</strong> approved real participant{approvedParticipantCount === 1 ? "" : "s"}
                    <span>{approvedRealTeamCount} real approved team{approvedRealTeamCount === 1 ? "" : "s"} • test bots excluded</span>
                  </div>
                </div>

                <div style={styles.communicationTemplateGrid}>
                  {Object.entries(PARTICIPANT_EMAIL_PREVIEWS).map(([type, preview]) => {
                    const alreadySent = broadcastAlreadySent(type);
                    const sending = participantEmailBusy === type;
                    return (
                      <div key={type} style={styles.communicationTemplateCard}>
                        <div style={styles.communicationTemplateLabel}>{preview.label}</div>
                        <div style={styles.communicationSubject}>{preview.subject}</div>
                        <div style={styles.communicationPreviewCopy}>{preview.intro}</div>
                        <div style={styles.communicationActions}>
                          <button
                            type="button"
                            style={styles.smallGhostButton}
                            onClick={() => setParticipantEmailPreview(type)}
                          >
                            Preview
                          </button>
                          <button
                            type="button"
                            style={alreadySent ? styles.disabledAdminButton : styles.smallPrimaryButton}
                            disabled={
                              sending ||
                              Boolean(participantEmailBusy) ||
                              alreadySent ||
                              !approvedParticipantCount ||
                              tournamentComplete
                            }
                            onClick={() => sendParticipantBroadcast(type)}
                          >
                            {sending
                              ? "Sending…"
                              : alreadySent
                              ? "Already Sent"
                              : type === "rules_prep"
                              ? "Send Rules & Prep"
                              : type === "day_before"
                              ? "Send Day-Before Reminder"
                              : "Send Tournament-Day Reminder"}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {participantBroadcasts.length ? (
                  <div style={styles.communicationHistory}>
                    <div style={styles.communicationHistoryTitle}>Recent participant email activity</div>
                    {participantBroadcasts.slice(0, 6).map((broadcast) => (
                      <div key={broadcast.id} style={styles.communicationHistoryRow}>
                        <div>
                          <strong>{broadcast.label || broadcast.type}</strong>
                          <span style={styles.communicationHistoryMeta}>
                            {broadcast.sentAt
                              ? new Date(broadcast.sentAt).toLocaleString()
                              : broadcast.createdAt
                              ? new Date(broadcast.createdAt).toLocaleString()
                              : ""}
                          </span>
                        </div>
                        <div style={styles.communicationDeliveryBadge}>
                          {broadcast.status === "sending"
                            ? "Sending…"
                            : `${broadcast.deliveredCount || 0}/${broadcast.recipientCount || 0} delivered`}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}

                {!approvedParticipantCount ? (
                  <div style={styles.simulatorHint}>
                    Email buttons unlock when at least one real approved participant is available. Test bots never count as recipients.
                  </div>
                ) : null}
              </div>

              {adminTeams.length ? (
                <div style={styles.adminTeamList}>
                  {adminTeams.map((team) => (
                    <div key={team.id} style={styles.adminTeamCard}>
                      <div style={styles.adminTeamInfo}>
                        <div style={styles.adminTeamNameRow}>
                          <div style={styles.inviteTeamName}>{team.name}</div>
                          {team.simulated ? <span style={styles.botTeamBadge}>TEST BOT</span> : null}
                        </div>
                        <div style={styles.inviteMeta}>
                          {team.members?.map((member) => `${member.username} (${memberStatusLabel(member.status)}${member.status === "accepted" ? ` / ${payoutEligibilityLabel(member.payoutEligibility)}` : ""})`).join(" • ")}
                        </div>
                        {team.payoutReadiness && !team.payoutReadiness.ready ? (
                          <div style={styles.adminPayoutWarning}>Payout-ready: {team.payoutReadiness.readyCount || 0}/3. Cannot approve until all 3 are ready.</div>
                        ) : null}
                        <div style={styles.adminStatusText}>{statusLabel(team.status)}</div>
                      </div>
                      <div style={styles.actionRow}>
                        {team.status === "ready_for_review" || team.status === "waitlisted" ? (
                          <button
                            type="button"
                            style={styles.smallPrimaryButton}
                            disabled={actionBusy || (team.payoutReadiness && !team.payoutReadiness.ready)}
                            onClick={() => adminTeamDecision(team.id, "approve")}
                          >
                            Approve
                          </button>
                        ) : null}
                        {team.status !== "approved" && team.status !== "waitlisted" ? (
                          <button
                            type="button"
                            style={styles.smallGhostButton}
                            disabled={actionBusy}
                            onClick={() => adminTeamDecision(team.id, "waitlist")}
                          >
                            Waitlist
                          </button>
                        ) : null}
                        {!["approved", "champion", "eliminated"].includes(team.status) ? (
                          <button
                            type="button"
                            style={styles.rejectButton}
                            disabled={actionBusy}
                            onClick={() => adminTeamDecision(team.id, "reject")}
                          >
                            Reject
                          </button>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={styles.emptyAdminBox}>No team registrations yet.</div>
              )}

              {tournamentLive || tournamentComplete ? (
                <div id="admin-live-scoring" style={styles.liveScoringSection}>
                  <div style={styles.sectionEyebrow}>LIVE BRACKET SCORING</div>
                  <h4 style={styles.liveScoringTitle}>Best-of-3 series control</h4>
                  <p style={styles.sectionCopy}>
                    Enter each series score from 0–2. When a team reaches 2 wins, the bracket advances that winner automatically and marks the losing team eliminated.
                  </p>

                  {scoreableMatches.length ? (
                    <div style={styles.scoreControlGrid}>
                      {scoreableMatches.map((match) => {
                        const draft = scoreDrafts?.[match.id] || {
                          scoreA: Number(match.scoreA || 0),
                          scoreB: Number(match.scoreB || 0),
                        };
                        const report = adminMatchReports.find((candidate) => candidate?.matchId === match.id) || null;
                        return (
                          <div key={match.id} style={styles.scoreControlCard}>
                            <div style={styles.scoreMatchLabel}>
                              {match.conference === "final"
                                ? "CHAMPIONSHIP"
                                : `${String(match.conference || "").toUpperCase()} • ROUND ${match.round}`}
                            </div>
                            {report ? (
                              <div
                                style={
                                  report.status === "disputed"
                                    ? styles.adminReportDisputed
                                    : ["awaiting_opponent_submission", "pending_confirmation"].includes(report.status)
                                    ? styles.adminReportPending
                                    : styles.adminReportResolved
                                }
                              >
                                Captain report: {matchReportStatusLabel(report.status)}
                                {report.disputeReason ? ` • ${report.disputeReason}` : ""}
                              </div>
                            ) : null}
                            <div style={styles.scoreTeamLine}>
                              <span style={styles.scoreTeamName}>{match.teamA?.name}</span>
                              <input
                                type="number"
                                min="0"
                                max="2"
                                inputMode="numeric"
                                value={draft.scoreA}
                                disabled={actionBusy || tournamentComplete}
                                onChange={(event) => updateScoreDraft(match.id, "scoreA", event.target.value)}
                                style={styles.scoreInput}
                              />
                            </div>
                            <div style={styles.scoreTeamLine}>
                              <span style={styles.scoreTeamName}>{match.teamB?.name}</span>
                              <input
                                type="number"
                                min="0"
                                max="2"
                                inputMode="numeric"
                                value={draft.scoreB}
                                disabled={actionBusy || tournamentComplete}
                                onChange={(event) => updateScoreDraft(match.id, "scoreB", event.target.value)}
                                style={styles.scoreInput}
                              />
                            </div>
                            {match.status === "complete" ? (
                              <div style={styles.matchCompleteText}>Winner advanced automatically</div>
                            ) : null}
                            {!tournamentComplete ? (
                              <button
                                type="button"
                                style={styles.saveScoreButton}
                                disabled={actionBusy}
                                onClick={() => saveMatchScore(match.id)}
                              >
                                Save Series Score
                              </button>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={styles.emptyAdminBox}>Waiting for the first complete matchup.</div>
                  )}
                </div>
              ) : null}
            </section>
          ) : null}

          <section style={styles.rulesSection}>
            <div style={styles.sectionEyebrow}>TOURNAMENT RULES • ENFORCED</div>
            <h3 style={styles.sectionTitle}>75 OVR means 75 OVR or below.</h3>
            <div style={styles.ruleGrid}>
              <RuleCard
                number="01"
                title="Maximum 75 OVR"
                body="Every player on the floor must be rated 75 OVR or lower. Players may report an opponent who appears to violate the cap."
              />
              <RuleCard
                number="02"
                title="Over 75 loses that game"
                body="Using a build above 75 OVR results in an automatic loss for that specific game. It is not an immediate tournament disqualification after the first act."
              />
              <RuleCard
                number="03"
                title="Correct it and continue"
                body="The team may continue the best-of-3 series by bringing out an eligible 75-or-lower build for the next game."
              />
              <RuleCard
                number="04"
                title="No eligible lineup means forfeit"
                body="If the team cannot field three eligible players at 75 OVR or below, the team forfeits the series."
              />
            </div>
          </section>

          <p style={styles.disclaimer}>
            This community-hosted event is not affiliated with, sponsored by, or endorsed by 2K,
            Take-Two Interactive, the NBA, the NBA 2K League, or related entities.
          </p>
        </div>
      </section>

      {rulesOpen ? (
        <div style={styles.rulesModalBackdrop} onMouseDown={() => setRulesOpen(false)}>
          <div style={styles.rulesModal} onMouseDown={(event) => event.stopPropagation()}>
            <div style={styles.rulesModalHeader}>
              <div>
                <div style={styles.sectionEyebrow}>GET ACTIVE TOURNAMENT</div>
                <h3 style={styles.rulesModalTitle}>Rules & How the Tournament Works</h3>
                <p style={styles.rulesModalSubcopy}>
                  This is the working tournament rules foundation. Material changes made before tournament day can be communicated to every approved participant by email.
                </p>
              </div>
              <button
                type="button"
                style={styles.disputeModalClose}
                onClick={() => setRulesOpen(false)}
                aria-label="Close tournament rules"
              >
                ×
              </button>
            </div>

            <div style={styles.rulesTabs}>
              {TOURNAMENT_RULE_TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  style={rulesTab === tab.id ? styles.rulesTabActive : styles.rulesTab}
                  onClick={() => setRulesTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div style={styles.rulesModalBody}>
              {rulesTab === "rules" ? (
                <>
                  <RuleModalSection title="Format">
                    16 teams compete in a playoff-style bracket: 8 East and 8 West. Every matchup is a best-of-3 series. The East champion and West champion meet in the final.
                  </RuleModalSection>
                  <RuleModalSection title="Team roster">
                    Each team has exactly 3 confirmed GAStreaming users. The captain creates the team, invites two existing GAStreaming usernames, and all three players must confirm before tournament approval.
                  </RuleModalSection>
                  <RuleModalSection title="Registration and the 48-hour withdrawal deadline">
                    Registration closes automatically when all 16 approved team slots are filled. Captains may withdraw an approved team until 48 hours before the announced tournament start time. A withdrawal before the deadline frees the slot for the first eligible waitlisted team or reopens registration if no replacement is ready. After the deadline, approved teams are locked in. A team that cannot play or fails to appear may forfeit its matchup and the opponent may advance after an admin ruling.
                  </RuleModalSection>
                  <RuleModalSection title="75 OVR maximum">
                    Every player on the floor must be 75 OVR or below. If a player uses a build above 75 OVR, that team automatically loses that specific game. The team may continue the series with an eligible lineup. If it cannot field three eligible players, it forfeits the series.
                  </RuleModalSection>
                  <RuleModalSection title="Crossplay and gamertags">
                    Crossplay is enabled. Platform and NBA 2K gamertag information is collected for match coordination and shown only to current opponents inside the private match lobby.
                  </RuleModalSection>
                  <RuleModalSection title="Prize">
                    The current prize is $300 total, paid as three separate $100 individual prizes to the three winning players. Every participant must complete GAStreaming Stripe Connect payout onboarding before their team can be approved. If Stripe requires age or guardian verification, that must be completed before tournament play.
                  </RuleModalSection>
                </>
              ) : null}

              {rulesTab === "flow" ? (
                <>
                  <RuleStep number="01" title="Captain registration and payout check">The captain enters the team name, their own gamertag/platform, and two GAStreaming usernames. If the captain is not already payout-ready, the registration draft is saved while Stripe Connect onboarding is completed.</RuleStep>
                  <RuleStep number="02" title="Teammates accept and qualify">Each invited teammate accepts the invitation, completes Stripe payout eligibility if needed, then enters their own NBA 2K gamertag and platform before confirming the roster spot.</RuleStep>
                  <RuleStep number="03" title="3/3 readiness and admin approval">A team reaches admin review only after all three players are confirmed, have player details on file, and are payout-ready. Approved teams fill the 16-team field and populate the live bracket. Registration closes automatically when the 16th approved team locks the field.</RuleStep>
                  <RuleStep number="04" title="Coordinate the matchup">When two teams are matched, players use their private match lobby to see opponent gamertags, coordinate Park invites, and communicate about the series.</RuleStep>
                  <RuleStep number="05" title="Report scores live or all at once">Captains may update the score after every game so the bracket reflects the series in real time, or wait until the best-of-3 is complete and submit the final result once.</RuleStep>
                  <RuleStep number="06" title="Both captains lock the final">A matching final 2–0 or 2–1 result from both captains permanently locks the series, advances the winner, and eliminates the losing team.</RuleStep>
                  <RuleStep number="07" title="Champion and automatic payout">The final winner is recorded, the tournament closes permanently, winner achievements are saved, and payout-ready champions receive individual $100 Stripe Connect payouts automatically.</RuleStep>
                </>
              ) : null}

              {rulesTab === "disputes" ? (
                <>
                  <RuleModalSection title="Automatic score conflict">
                    If both captains report conflicting scores at the same stage, the matchup is paused and sent to admin review. Nobody advances until the issue is resolved.
                  </RuleModalSection>
                  <RuleModalSection title="Manual dispute button">
                    A captain may use Dispute Match / Report Violation for an incorrect score, over-75 OVR player, no-show, unauthorized roster, cheating or exploit abuse, or another rule issue.
                  </RuleModalSection>
                  <RuleModalSection title="Proof">
                    The reporting captain can provide a brief explanation and upload up to 3 screenshots. The tournament admin receives a site notification and reviews the issue on the separate Admin Review Center page.
                  </RuleModalSection>
                  <RuleModalSection title="While under review">
                    The disputed match is paused. No team advances until admin applies an official score, resumes the match, or makes another ruling allowed by the tournament rules.
                  </RuleModalSection>
                </>
              ) : null}

              {rulesTab === "match-day" ? (
                <>
                  <RuleModalSection title="Before your series">
                    Sign in to GAStreaming, confirm your roster details are current, have your eligible 75-or-lower build ready, and open your private match lobby when a matchup is active. Approved teams are locked after the 48-hour withdrawal deadline; a late cancellation or no-show may be ruled a forfeit.
                  </RuleModalSection>
                  <RuleModalSection title="Park invites and communication">
                    Use the private match lobby to exchange gamertags and coordinate who sends the Park invite. Keep communication focused on getting the series played cleanly.
                  </RuleModalSection>
                  <RuleModalSection title="Score reporting">
                    Captains should report honestly. Scores can be updated after each game for live bracket tracking or entered once after the complete series. Save screenshots whenever proof may be useful.
                  </RuleModalSection>
                  <RuleModalSection title="Notifications">
                    Watch the GAStreaming bell for team invites, score activity, disputes, admin rulings, tournament completion, and prize updates.
                  </RuleModalSection>
                  <RuleModalSection title="Tournament completion">
                    Once the championship result is final, registration and re-entry are permanently closed. The final bracket remains as tournament history and the winner payout process begins.
                  </RuleModalSection>
                </>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {participantEmailPreview ? (
        <div style={styles.rulesModalBackdrop} onMouseDown={() => setParticipantEmailPreview("")}>
          <div style={styles.emailPreviewModal} onMouseDown={(event) => event.stopPropagation()}>
            <div style={styles.rulesModalHeader}>
              <div>
                <div style={styles.sectionEyebrow}>EMAIL PREVIEW</div>
                <h3 style={styles.rulesModalTitle}>{PARTICIPANT_EMAIL_PREVIEWS[participantEmailPreview]?.label}</h3>
                <p style={styles.rulesModalSubcopy}>
                  Preview only. Nothing is sent until you use the send button in Participant Communications and confirm the recipient count.
                </p>
              </div>
              <button
                type="button"
                style={styles.disputeModalClose}
                onClick={() => setParticipantEmailPreview("")}
                aria-label="Close email preview"
              >
                ×
              </button>
            </div>
            <div style={styles.emailPreviewCard}>
              <div style={styles.emailPreviewSubject}>
                Subject: {PARTICIPANT_EMAIL_PREVIEWS[participantEmailPreview]?.subject}
              </div>
              <p style={styles.emailPreviewGreeting}>What's good, [participant username],</p>
              <p style={styles.emailPreviewText}>{PARTICIPANT_EMAIL_PREVIEWS[participantEmailPreview]?.intro}</p>
              <div style={styles.emailPreviewDetails}>
                <strong>Get Active Tournament</strong>
                <span>16 teams • 8 East / 8 West • 3v3 Park</span>
                <span>75 OVR maximum • Best 2 out of 3 • Crossplay enabled</span>
                <span>$300 team prize • $100 per winning player</span>
              </div>
              <ul style={styles.emailPreviewList}>
                <li>Stay at 75 OVR or below; an over-75 build costs that team the specific game.</li>
                <li>Use the private match lobby to exchange gamertags and coordinate Park invites.</li>
                <li>Captains can report after each game or submit the full series result at the end.</li>
                <li>Conflicting scores or violations can be disputed with explanation and screenshot proof.</li>
              </ul>
              <div style={styles.emailPreviewButton}>View Tournament & Rules</div>
            </div>
          </div>
        </div>
      ) : null}

      {lobbyOpen ? (
        <div style={styles.lobbyBackdrop} onMouseDown={() => setLobbyOpen(false)}>
          <div style={styles.lobbyModal} onMouseDown={(event) => event.stopPropagation()}>
            <div style={styles.lobbyHeader}>
              <div>
                <div style={styles.sectionEyebrow}>PRIVATE MATCH LOBBY</div>
                <h3 style={styles.lobbyTitle}>
                  {lobbyData?.match?.teamA?.name || "Team A"} vs {lobbyData?.match?.teamB?.name || "Team B"}
                </h3>
                <div style={styles.lobbyPrivacyCopy}>
                  Gamertags and platforms here are visible only to players in this active matchup and tournament admins.
                </div>
              </div>
              <button type="button" style={styles.disputeModalClose} onClick={() => setLobbyOpen(false)}>×</button>
            </div>

            {lobbyLoading && !lobbyData ? <div style={styles.loadingBox}>Loading match lobby…</div> : null}
            {lobbyError ? <div style={styles.errorBox}>{lobbyError}</div> : null}

            {lobbyData ? (
              <>
                <div style={styles.lobbyRosters}>
                  {[lobbyData?.rosters?.teamA, lobbyData?.rosters?.teamB].filter(Boolean).map((team) => (
                    <div key={team.id} style={styles.lobbyRosterCard}>
                      <div style={styles.lobbyRosterTeam}>{team.name}</div>
                      {(team.members || []).map((member) => (
                        <div key={member.userId} style={styles.lobbyPlayerRow}>
                          <span style={styles.lobbyPlayerName}>@{member.username}</span>
                          <span style={styles.lobbyGamertag}>{member.gamertag || "Gamertag not added"}</span>
                          <span style={styles.lobbyPlatform}>{member.platform || "Platform not added"}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>

                <div style={styles.lobbyChatBox}>
                  {(lobbyData.messages || []).length ? (
                    (lobbyData.messages || []).map((entry) => (
                      <div key={entry.id} style={styles.lobbyMessageRow}>
                        <div style={styles.lobbyMessageMeta}>@{entry.username} • {entry.createdAt ? new Date(entry.createdAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : ""}</div>
                        <div style={styles.lobbyMessageBody}>{entry.body}</div>
                      </div>
                    ))
                  ) : (
                    <div style={styles.emptyAdminBox}>No messages yet. Use this lobby to exchange Park invite details and coordinate when to play.</div>
                  )}
                </div>

                <form onSubmit={sendLobbyMessage} style={styles.lobbyComposer}>
                  <input
                    type="text"
                    maxLength={500}
                    value={lobbyMessage}
                    onChange={(event) => setLobbyMessage(event.target.value)}
                    style={styles.input}
                    placeholder="Message both teams…"
                  />
                  <button type="submit" style={styles.smallPrimaryButton}>Send</button>
                </form>
              </>
            ) : null}
          </div>
        </div>
      ) : null}

      {disputeOpen && captainMatch ? (
        <div style={styles.disputeModalBackdrop} onMouseDown={() => !disputeSubmitting && setDisputeOpen(false)}>
          <div style={styles.disputeModal} onMouseDown={(event) => event.stopPropagation()}>
            <div style={styles.disputeModalHeader}>
              <div>
                <div style={styles.sectionEyebrow}>CAPTAIN DISPUTE</div>
                <h3 style={styles.disputeModalTitle}>Report a match issue</h3>
                <p style={styles.disputeModalSubcopy}>
                  {captainMatch.teamA?.name} vs {captainMatch.teamB?.name}. Upload screenshots when possible. The match pauses until admin review is complete.
                </p>
              </div>
              <button
                type="button"
                style={styles.disputeModalClose}
                disabled={disputeSubmitting}
                onClick={() => setDisputeOpen(false)}
                aria-label="Close dispute form"
              >
                ×
              </button>
            </div>

            <form onSubmit={submitMatchDispute} style={styles.disputeForm}>
              <label style={styles.label}>
                What are you disputing?
                <select
                  value={disputeForm.reasonType}
                  onChange={(event) => setDisputeForm((current) => ({ ...current, reasonType: event.target.value }))}
                  style={styles.input}
                  disabled={disputeSubmitting}
                >
                  <option value="incorrect_score">Incorrect score or record</option>
                  <option value="over_75_ovr">Player over 75 OVR</option>
                  <option value="no_show">Opponent no-show</option>
                  <option value="unauthorized_roster">Unauthorized player / roster violation</option>
                  <option value="cheating_exploit">Cheating or exploit abuse</option>
                  <option value="other">Other issue</option>
                </select>
              </label>

              <label style={styles.label}>
                Brief explanation
                <textarea
                  value={disputeForm.explanation}
                  onChange={(event) => setDisputeForm((current) => ({ ...current, explanation: event.target.value }))}
                  style={styles.disputeTextarea}
                  maxLength={1200}
                  rows={5}
                  disabled={disputeSubmitting}
                  placeholder="Explain what happened, which game it happened in, and what you believe the correct outcome should be."
                  required
                />
              </label>

              <label style={styles.label}>
                Screenshot proof — up to 3 images
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  disabled={disputeSubmitting}
                  onChange={(event) => setDisputeFiles(Array.from(event.target.files || []).slice(0, 3))}
                  style={styles.fileInput}
                />
              </label>

              {disputeFiles.length ? (
                <div style={styles.selectedProofList}>
                  {disputeFiles.map((file) => (
                    <div key={`${file.name}-${file.size}`} style={styles.selectedProofItem}>
                      {file.name}
                    </div>
                  ))}
                </div>
              ) : null}

              <div style={styles.disputeModalActions}>
                <button
                  type="button"
                  style={styles.smallGhostButton}
                  disabled={disputeSubmitting}
                  onClick={() => setDisputeOpen(false)}
                >
                  Cancel
                </button>
                <button type="submit" style={styles.disputeSubmitButton} disabled={disputeSubmitting}>
                  {disputeSubmitting ? "Submitting…" : "Submit Dispute for Admin Review"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div style={styles.detailCard}>
      <div style={styles.detailLabel}>{label}</div>
      <div style={styles.detailValue}>{value}</div>
    </div>
  );
}

function RuleCard({ number, title, body }) {
  return (
    <div style={styles.ruleCard}>
      <div style={styles.ruleNumber}>{number}</div>
      <div style={styles.ruleTitle}>{title}</div>
      <div style={styles.ruleBody}>{body}</div>
    </div>
  );
}

function RuleModalSection({ title, children }) {
  return (
    <div style={styles.ruleModalSection}>
      <div style={styles.ruleModalSectionTitle}>{title}</div>
      <div style={styles.ruleModalSectionBody}>{children}</div>
    </div>
  );
}

function FlowStep({ number, title, copy }) {
  return (
    <div style={styles.registrationFlowStep}>
      <div style={styles.flowStepBadge}>{number}</div>
      <div>
        <div style={styles.flowSectionTitle}>{title}</div>
        <div style={styles.flowStepCopy}>{copy}</div>
      </div>
    </div>
  );
}

function RuleStep({ number, title, children }) {
  return (
    <div style={styles.ruleStep}>
      <div style={styles.ruleStepNumber}>{number}</div>
      <div>
        <div style={styles.ruleModalSectionTitle}>{title}</div>
        <div style={styles.ruleModalSectionBody}>{children}</div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    maxWidth: 1180,
    margin: "0 auto",
    padding: "28px 14px 80px",
  },
  hero: {
    borderRadius: 24,
    padding: "28px 22px",
    border: "1px solid rgba(255,255,255,0.12)",
    background:
      "radial-gradient(900px 420px at 20% 0%, rgba(255,0,64,0.20), transparent 60%), rgba(10,10,14,0.78)",
    boxShadow: "0 22px 70px rgba(0,0,0,0.45)",
  },
  kicker: {
    color: "#ffb3b3",
    textTransform: "uppercase",
    letterSpacing: 3,
    fontSize: 12,
    fontWeight: 900,
  },
  title: {
    margin: "8px 0 0",
    fontSize: "clamp(38px, 7vw, 72px)",
    lineHeight: 0.95,
    fontFamily: 'Georgia, "Times New Roman", serif',
  },
  subtitle: {
    margin: "16px 0 0",
    color: "rgba(255,255,255,0.82)",
    lineHeight: 1.55,
    fontWeight: 700,
  },
  eventCard: {
    marginTop: 18,
    borderRadius: 24,
    overflow: "hidden",
    background: "rgba(8,8,12,0.80)",
    border: "1px solid rgba(255,255,255,0.12)",
    boxShadow: "0 22px 70px rgba(0,0,0,0.5)",
  },
  imageWrap: {
    position: "relative",
    aspectRatio: "1672 / 835",
    overflow: "hidden",
    background: "#06070a",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
  },
  bracketOverlay: {
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
  },
  bracketTeamSlot: {
    position: "absolute",
    width: "9.0%",
    height: "6.7%",
    transform: "translateY(-58%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 5px",
    boxSizing: "border-box",
    overflow: "hidden",
    color: "#fff",
    textShadow: "0 1px 3px #000, 0 0 8px rgba(255,255,255,0.18)",
    fontWeight: 900,
    textAlign: "center",
  },
  bracketTeamRow: {
    width: "100%",
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "3px",
  },
  bracketTeamRowMirrored: {
    flexDirection: "row",
  },
  bracketNameWrap: {
    minWidth: 0,
    flex: "1 1 auto",
    display: "flex",
    alignItems: "center",
    gap: "2px",
    overflow: "hidden",
  },
  bracketNameWrapMirrored: {
    justifyContent: "flex-end",
    textAlign: "right",
  },
  bracketSeed: {
    flex: "0 0 auto",
    color: "#ff6b6b",
    fontSize: "clamp(5px, 0.44vw, 7px)",
    lineHeight: 1,
  },
  bracketTeamText: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: "clamp(6px, 0.58vw, 9px)",
    lineHeight: 1,
    letterSpacing: "-0.01em",
  },
  bracketScore: {
    flex: "0 0 auto",
    minWidth: "0.9em",
    fontSize: "clamp(6px, 0.62vw, 10px)",
    lineHeight: 1,
    color: "#fff",
    textAlign: "right",
  },
  bracketFinalLayer: {
    position: "absolute",
    top: 0,
    height: "100%",
    width: "8.8%",
    pointerEvents: "none",
  },
  bracketFinalParticipant: {
    position: "absolute",
    left: 0,
    width: "100%",
    height: "7.5%",
    transform: "translateY(-50%)",
    display: "flex",
    alignItems: "center",
    padding: "0 5px",
    boxSizing: "border-box",
    overflow: "hidden",
    color: "#fff",
    textShadow: "0 1px 3px #000, 0 0 8px rgba(255,255,255,0.18)",
    fontWeight: 900,
  },
  bracketChampionSlot: {
    position: "absolute",
    top: "63.9%",
    width: "8.1%",
    height: "7.8%",
    transform: "translateY(-50%)",
    display: "flex",
    alignItems: "center",
    padding: "0 5px",
    boxSizing: "border-box",
    overflow: "hidden",
    color: "#fff",
    textShadow: "0 1px 3px #000, 0 0 8px rgba(255,255,255,0.18)",
    fontWeight: 900,
  },
  bracketCompactRow: {
    width: "100%",
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "2px",
  },
  bracketCompactTeamText: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: "clamp(4.5px, 0.46vw, 7px)",
    lineHeight: 1,
  },
  bracketCompactScore: {
    flex: "0 0 auto",
    fontSize: "clamp(5px, 0.5vw, 8px)",
    lineHeight: 1,
  },
  image: {
    display: "block",
    width: "100%",
    height: "auto",
    maxHeight: 620,
    objectFit: "contain",
  },
  content: {
    padding: "22px",
  },
  completionLockNotice: {
    marginTop: 14,
    border: "1px solid rgba(255, 77, 77, 0.42)",
    borderRadius: 14,
    padding: "13px 15px",
    background: "linear-gradient(135deg, rgba(96, 0, 18, 0.42), rgba(18, 4, 8, 0.78))",
    color: "rgba(255,255,255,0.84)",
    lineHeight: 1.55,
  },
  statusRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  },
  statusPill: {
    display: "inline-flex",
    padding: "7px 10px",
    borderRadius: 999,
    background: "rgba(225,6,19,0.14)",
    border: "1px solid rgba(255,77,77,0.34)",
    color: "#ff8e8e",
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: 1.5,
  },
  testModeBadge: {
    display: "inline-flex",
    padding: "7px 10px",
    borderRadius: 999,
    background: "rgba(255,190,0,0.14)",
    border: "1px solid rgba(255,210,90,0.38)",
    color: "#ffd86a",
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: 1.2,
  },
  spotCounter: {
    color: "rgba(255,255,255,0.6)",
    fontSize: 12,
    fontWeight: 800,
  },
  eventTitle: {
    margin: "14px 0 0",
    fontSize: "clamp(28px, 5vw, 46px)",
    fontFamily: 'Georgia, "Times New Roman", serif',
  },
  prize: {
    marginTop: 8,
    color: "#ff4d4d",
    fontSize: "clamp(18px, 3vw, 26px)",
    fontWeight: 900,
  },
  detailsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 12,
    marginTop: 20,
  },
  detailCard: {
    borderRadius: 16,
    padding: "14px 15px",
    background: "rgba(0,0,0,0.34)",
    border: "1px solid rgba(255,255,255,0.10)",
  },
  detailLabel: {
    color: "rgba(255,255,255,0.55)",
    textTransform: "uppercase",
    letterSpacing: 1.2,
    fontSize: 10,
    fontWeight: 900,
  },
  detailValue: {
    marginTop: 6,
    color: "#fff",
    fontSize: 15,
    fontWeight: 900,
  },
  notice: {
    marginTop: 18,
    padding: "14px 16px",
    borderRadius: 16,
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.10)",
    color: "rgba(255,255,255,0.78)",
    lineHeight: 1.55,
  },
  errorBox: {
    marginTop: 16,
    padding: "12px 14px",
    borderRadius: 14,
    background: "rgba(180,0,0,0.24)",
    border: "1px solid rgba(255,70,70,0.45)",
    color: "#ffd4d4",
    fontWeight: 800,
  },
  successBox: {
    marginTop: 16,
    padding: "12px 14px",
    borderRadius: 14,
    background: "rgba(20,145,80,0.18)",
    border: "1px solid rgba(80,220,135,0.38)",
    color: "#c8ffdf",
    fontWeight: 800,
  },
  loadingBox: {
    marginTop: 18,
    padding: "22px",
    borderRadius: 18,
    background: "rgba(0,0,0,0.28)",
    border: "1px solid rgba(255,255,255,0.08)",
    color: "rgba(255,255,255,0.7)",
  },
  registrationBox: {
    marginTop: 20,
    padding: "20px",
    borderRadius: 20,
    background: "linear-gradient(145deg, rgba(70,0,10,0.22), rgba(0,0,0,0.42))",
    border: "1px solid rgba(255,90,90,0.18)",
  },
  sectionEyebrow: {
    color: "#ff9a9a",
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: 1.8,
  },
  sectionTitle: {
    margin: "7px 0 0",
    fontSize: "clamp(22px, 4vw, 32px)",
    fontFamily: 'Georgia, "Times New Roman", serif',
  },
  sectionCopy: {
    margin: "8px 0 0",
    color: "rgba(255,255,255,0.72)",
    lineHeight: 1.55,
  },
  form: {
    marginTop: 18,
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: 14,
  },
  label: {
    display: "grid",
    gap: 7,
    color: "rgba(255,255,255,0.76)",
    fontSize: 12,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: 0.7,
  },
  fieldHelp: {
    color: "rgba(255,255,255,0.56)",
    fontSize: 11,
    fontWeight: 700,
    lineHeight: 1.45,
    textTransform: "none",
    letterSpacing: 0,
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.16)",
    background: "rgba(0,0,0,0.55)",
    color: "#fff",
    padding: "12px 13px",
    outline: "none",
    fontSize: 15,
  },
  cta: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: 18,
    padding: "12px 18px",
    borderRadius: 999,
    border: "1px solid #ff4d4d",
    background: "linear-gradient(180deg, #e10613, #8c0000)",
    color: "#fff",
    textDecoration: "none",
    fontWeight: 900,
    cursor: "pointer",
  },
  ctaDisabled: {
    opacity: 0.7,
    cursor: "default",
  },
  teamHeadingRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 14,
    flexWrap: "wrap",
  },
  teamStatusBadge: {
    borderRadius: 999,
    padding: "7px 10px",
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.12)",
    color: "#fff",
    fontSize: 11,
    fontWeight: 900,
  },
  rosterGrid: {
    marginTop: 16,
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 12,
  },
  rosterCard: {
    padding: "14px",
    borderRadius: 16,
    background: "rgba(0,0,0,0.38)",
    border: "1px solid rgba(255,255,255,0.09)",
  },
  rosterName: {
    color: "#fff",
    fontWeight: 900,
    fontSize: 16,
  },
  rosterRole: {
    marginTop: 3,
    color: "rgba(255,255,255,0.5)",
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: 1,
  },
  confirmedText: {
    marginTop: 10,
    color: "#83f0ae",
    fontWeight: 900,
    fontSize: 12,
  },
  pendingText: {
    marginTop: 10,
    color: "#ffd27d",
    fontWeight: 900,
    fontSize: 12,
  },
  declinedText: {
    marginTop: 10,
    color: "#ff8080",
    fontWeight: 900,
    fontSize: 12,
  },
  inviteList: {
    marginTop: 14,
    display: "grid",
    gap: 10,
  },
  inviteCard: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 14,
    flexWrap: "wrap",
    padding: "14px",
    borderRadius: 16,
    background: "rgba(0,0,0,0.38)",
    border: "1px solid rgba(255,255,255,0.09)",
  },
  inviteTeamName: {
    color: "#fff",
    fontWeight: 900,
    fontSize: 18,
  },
  inviteMeta: {
    marginTop: 4,
    color: "rgba(255,255,255,0.58)",
    fontSize: 12,
  },
  actionRow: {
    display: "flex",
    gap: 8,
  },
  smallPrimaryButton: {
    border: "1px solid #ff4d4d",
    background: "linear-gradient(180deg, #e10613, #8c0000)",
    color: "#fff",
    borderRadius: 999,
    padding: "9px 13px",
    fontWeight: 900,
    cursor: "pointer",
  },
  smallGhostButton: {
    border: "1px solid rgba(255,255,255,0.16)",
    background: "rgba(255,255,255,0.05)",
    color: "#fff",
    borderRadius: 999,
    padding: "9px 13px",
    fontWeight: 900,
    cursor: "pointer",
  },
  captainActions: {
    marginTop: 18,
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  },
  captainActionHint: {
    color: "rgba(255,255,255,0.66)",
    fontSize: 13,
    lineHeight: 1.45,
    maxWidth: 680,
  },
  adminHeaderActions: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 10,
  },
  startTournamentButton: {
    border: "1px solid rgba(120,255,170,0.45)",
    background: "linear-gradient(135deg, rgba(20,150,80,0.95), rgba(5,90,50,0.95))",
    color: "#fff",
    borderRadius: 999,
    padding: "11px 16px",
    fontWeight: 900,
    cursor: "pointer",
  },
  disabledAdminButton: {
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(255,255,255,0.06)",
    color: "rgba(255,255,255,0.45)",
    borderRadius: 999,
    padding: "11px 16px",
    fontWeight: 900,
    cursor: "not-allowed",
  },
  liveScoringSection: {
    marginTop: 22,
    paddingTop: 22,
    borderTop: "1px solid rgba(255,255,255,0.10)",
  },
  liveScoringTitle: {
    margin: "6px 0 4px",
    fontSize: "clamp(24px, 3vw, 36px)",
    fontFamily: 'Georgia, "Times New Roman", serif',
  },
  scoreControlGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: 12,
    marginTop: 16,
  },
  scoreControlCard: {
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(0,0,0,0.34)",
    borderRadius: 16,
    padding: 14,
  },
  scoreMatchLabel: {
    color: "#ff9a9a",
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: "0.12em",
    marginBottom: 10,
  },
  scoreTeamLine: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    marginTop: 8,
  },
  scoreTeamName: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontWeight: 900,
  },
  scoreInput: {
    width: 58,
    borderRadius: 10,
    border: "1px solid rgba(255,255,255,0.18)",
    background: "rgba(0,0,0,0.5)",
    color: "#fff",
    padding: "8px 10px",
    fontWeight: 900,
    textAlign: "center",
  },
  matchCompleteText: {
    marginTop: 10,
    color: "#7fffa8",
    fontSize: 12,
    fontWeight: 800,
  },
  saveScoreButton: {
    width: "100%",
    marginTop: 12,
    border: "1px solid rgba(255,80,90,0.6)",
    background: "linear-gradient(135deg, #d90a1b, #8d0711)",
    color: "#fff",
    borderRadius: 12,
    padding: "10px 12px",
    fontWeight: 900,
    cursor: "pointer",
  },
  cancelButton: {
    marginTop: 16,
    border: "1px solid rgba(255,255,255,0.15)",
    background: "rgba(255,255,255,0.04)",
    color: "rgba(255,255,255,0.72)",
    borderRadius: 999,
    padding: "9px 13px",
    fontWeight: 800,
    cursor: "pointer",
  },

  captainMatchSection: {
    marginTop: 20,
    paddingTop: 18,
    borderTop: "1px solid rgba(255,255,255,0.10)",
  },
  captainMatchTitle: {
    margin: "6px 0 0",
    fontSize: "clamp(22px, 3vw, 34px)",
    fontFamily: 'Georgia, "Times New Roman", serif',
  },
  captainMatchMeta: {
    marginTop: 12,
    color: "#ff9a9a",
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: "0.12em",
  },
  captainMatchStatusBox: {
    marginTop: 12,
    padding: "14px 16px",
    borderRadius: 14,
    background: "rgba(0,0,0,0.32)",
    border: "1px solid rgba(255,255,255,0.10)",
    color: "rgba(255,255,255,0.72)",
  },
  captainResultGrid: {
    display: "grid",
    gap: 10,
    marginTop: 12,
  },
  captainResultTeamRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "12px 14px",
    borderRadius: 14,
    background: "rgba(0,0,0,0.36)",
    border: "1px solid rgba(255,255,255,0.10)",
  },
  captainResultTeamName: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontWeight: 900,
  },
  captainResultInput: {
    width: 60,
    flex: "0 0 60px",
    borderRadius: 10,
    border: "1px solid rgba(255,255,255,0.18)",
    background: "rgba(0,0,0,0.5)",
    color: "#fff",
    padding: "9px 10px",
    fontWeight: 900,
    textAlign: "center",
  },
  captainResultHelp: {
    margin: "10px 0 0",
    color: "rgba(255,255,255,0.62)",
    fontSize: 13,
    lineHeight: 1.5,
  },
  captainReportFeedback: {
    marginTop: 10,
    padding: "10px 12px",
    borderRadius: 12,
    background: "rgba(160, 20, 25, 0.18)",
    border: "1px solid rgba(255, 105, 110, 0.30)",
    color: "#ffd4d6",
    fontSize: 13,
    fontWeight: 800,
    lineHeight: 1.45,
  },
  submitCaptainResultButton: {
    marginTop: 12,
    border: "1px solid rgba(255,80,90,0.6)",
    background: "linear-gradient(135deg, #d90a1b, #8d0711)",
    color: "#fff",
    borderRadius: 12,
    padding: "11px 15px",
    fontWeight: 900,
    cursor: "pointer",
  },
  matchReportStatusCard: {
    marginTop: 12,
    padding: "14px",
    borderRadius: 14,
    background: "rgba(40,0,8,0.38)",
    border: "1px solid rgba(255,110,120,0.28)",
  },
  matchReportStatusTitle: {
    color: "#ffaaaa",
    fontWeight: 900,
    fontSize: 13,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  matchReportStatusCopy: {
    marginTop: 7,
    color: "rgba(255,255,255,0.78)",
    lineHeight: 1.45,
    fontSize: 13,
  },
  disputeReasonText: {
    marginTop: 8,
    color: "#ffd0d0",
    fontSize: 13,
    lineHeight: 1.45,
  },
  captainResponseActions: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    marginTop: 12,
  },
  confirmResultButton: {
    border: "1px solid rgba(120,255,170,0.45)",
    background: "linear-gradient(135deg, rgba(20,150,80,0.95), rgba(5,90,50,0.95))",
    color: "#fff",
    borderRadius: 999,
    padding: "10px 14px",
    fontWeight: 900,
    cursor: "pointer",
  },
  disputeResultButton: {
    border: "1px solid rgba(255,100,100,0.38)",
    background: "rgba(120,0,0,0.22)",
    color: "#ffb0b0",
    borderRadius: 999,
    padding: "10px 14px",
    fontWeight: 900,
    cursor: "pointer",
  },
  awaitingConfirmationText: {
    marginTop: 10,
    color: "#ffd27a",
    fontSize: 13,
    fontWeight: 800,
  },
  latestResultStrip: {
    marginTop: 10,
    color: "rgba(255,255,255,0.58)",
    fontSize: 12,
  },
  adminReportPending: {
    marginBottom: 10,
    padding: "7px 9px",
    borderRadius: 10,
    color: "#ffd27a",
    background: "rgba(150,100,0,0.14)",
    border: "1px solid rgba(255,210,122,0.24)",
    fontSize: 12,
    lineHeight: 1.35,
  },
  adminReportDisputed: {
    marginBottom: 10,
    padding: "7px 9px",
    borderRadius: 10,
    color: "#ffaaaa",
    background: "rgba(140,0,0,0.18)",
    border: "1px solid rgba(255,100,100,0.28)",
    fontSize: 12,
    lineHeight: 1.35,
  },
  adminReportResolved: {
    marginBottom: 10,
    padding: "7px 9px",
    borderRadius: 10,
    color: "#9cffb7",
    background: "rgba(0,120,55,0.14)",
    border: "1px solid rgba(120,255,170,0.22)",
    fontSize: 12,
    lineHeight: 1.35,
  },
  simulatorPanel: {
    marginTop: 18,
    padding: "18px",
    borderRadius: 18,
    background: "linear-gradient(145deg, rgba(60,42,0,0.28), rgba(20,0,5,0.72))",
    border: "1px solid rgba(255,205,80,0.28)",
  },
  simulatorHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 14,
    flexWrap: "wrap",
  },
  simulatorEyebrow: {
    color: "#ffd86a",
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: 1.6,
  },
  simulatorTitle: {
    margin: "6px 0 0",
    fontSize: "clamp(22px, 3vw, 30px)",
    fontFamily: 'Georgia, "Times New Roman", serif',
  },
  simulatorCopy: {
    margin: "8px 0 0",
    maxWidth: 760,
    color: "rgba(255,255,255,0.70)",
    lineHeight: 1.5,
  },
  testModeActivePill: {
    borderRadius: 999,
    padding: "8px 11px",
    background: "rgba(255,190,0,0.16)",
    border: "1px solid rgba(255,215,100,0.42)",
    color: "#ffe08a",
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: 1.1,
  },
  testModeIdlePill: {
    borderRadius: 999,
    padding: "8px 11px",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.12)",
    color: "rgba(255,255,255,0.62)",
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: 1.1,
  },
  simulatorStats: {
    marginTop: 14,
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: 10,
  },
  simulatorActions: {
    marginTop: 14,
    display: "flex",
    gap: 9,
    flexWrap: "wrap",
  },
  simulatorPrimaryButton: {
    marginTop: 14,
    border: "1px solid rgba(255,210,90,0.48)",
    background: "linear-gradient(135deg, #b87500, #7b3500)",
    color: "#fff",
    borderRadius: 999,
    padding: "10px 14px",
    fontWeight: 900,
    cursor: "pointer",
  },
  simulatorConfirmButton: {
    border: "1px solid rgba(120,255,170,0.40)",
    background: "rgba(0,120,60,0.30)",
    color: "#a9ffc5",
    borderRadius: 999,
    padding: "10px 14px",
    fontWeight: 900,
    cursor: "pointer",
  },
  simulatorDisputeButton: {
    border: "1px solid rgba(255,100,100,0.38)",
    background: "rgba(130,0,0,0.25)",
    color: "#ffaaaa",
    borderRadius: 999,
    padding: "10px 14px",
    fontWeight: 900,
    cursor: "pointer",
  },
  simulatorResolveButton: {
    border: "1px solid rgba(120,180,255,0.38)",
    background: "rgba(20,70,135,0.28)",
    color: "#b9d7ff",
    borderRadius: 999,
    padding: "10px 14px",
    fontWeight: 900,
    cursor: "pointer",
  },
  simulatorRunButton: {
    border: "1px solid rgba(215,140,255,0.42)",
    background: "linear-gradient(135deg, rgba(110,35,145,0.9), rgba(60,10,85,0.9))",
    color: "#f0c8ff",
    borderRadius: 999,
    padding: "10px 14px",
    fontWeight: 900,
    cursor: "pointer",
  },
  simulatorResetButton: {
    border: "1px solid rgba(255,255,255,0.16)",
    background: "rgba(255,255,255,0.05)",
    color: "rgba(255,255,255,0.78)",
    borderRadius: 999,
    padding: "10px 14px",
    fontWeight: 900,
    cursor: "pointer",
  },
  simulatorHint: {
    marginTop: 12,
    padding: "11px 13px",
    borderRadius: 12,
    background: "rgba(255,210,90,0.08)",
    border: "1px solid rgba(255,210,90,0.18)",
    color: "#ffe6a6",
    fontSize: 13,
    lineHeight: 1.45,
  },
  simulatorLog: {
    marginTop: 14,
    padding: "12px",
    borderRadius: 14,
    background: "rgba(0,0,0,0.28)",
    border: "1px solid rgba(255,255,255,0.08)",
  },
  simulatorLogTitle: {
    color: "#fff",
    fontWeight: 900,
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: 1,
  },
  simulatorLogRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    marginTop: 8,
    paddingTop: 8,
    borderTop: "1px solid rgba(255,255,255,0.06)",
    color: "rgba(255,255,255,0.72)",
    fontSize: 12,
    lineHeight: 1.4,
  },
  simulatorLogTime: {
    flex: "0 0 auto",
    color: "rgba(255,255,255,0.42)",
    whiteSpace: "nowrap",
  },
  adminTeamNameRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  botTeamBadge: {
    borderRadius: 999,
    padding: "4px 7px",
    background: "rgba(255,190,0,0.12)",
    border: "1px solid rgba(255,215,100,0.30)",
    color: "#ffd86a",
    fontSize: 9,
    fontWeight: 900,
    letterSpacing: 0.8,
  },

  adminSection: {
    marginTop: 22,
    padding: "20px",
    borderRadius: 20,
    background: "linear-gradient(145deg, rgba(28,28,34,0.92), rgba(20,0,5,0.82))",
    border: "1px solid rgba(255,120,120,0.24)",
  },
  adminHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
    flexWrap: "wrap",
  },
  adminStats: {
    marginTop: 16,
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
    gap: 10,
  },
  adminTeamList: {
    marginTop: 16,
    display: "grid",
    gap: 10,
  },
  adminTeamCard: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 14,
    flexWrap: "wrap",
    padding: "14px",
    borderRadius: 16,
    background: "rgba(0,0,0,0.4)",
    border: "1px solid rgba(255,255,255,0.09)",
  },
  adminTeamInfo: {
    minWidth: 0,
    flex: "1 1 420px",
  },
  adminStatusText: {
    marginTop: 7,
    color: "#ff9a9a",
    fontWeight: 900,
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: 0.8,
  },
  adminCloseButton: {
    border: "1px solid rgba(255,255,255,0.16)",
    background: "rgba(255,255,255,0.06)",
    color: "#fff",
    borderRadius: 999,
    padding: "10px 14px",
    fontWeight: 900,
    cursor: "pointer",
  },
  rejectButton: {
    border: "1px solid rgba(255,90,90,0.3)",
    background: "rgba(125,0,0,0.18)",
    color: "#ffadad",
    borderRadius: 999,
    padding: "9px 13px",
    fontWeight: 900,
    cursor: "pointer",
  },
  emptyAdminBox: {
    marginTop: 16,
    padding: "18px",
    borderRadius: 16,
    background: "rgba(0,0,0,0.3)",
    border: "1px dashed rgba(255,255,255,0.12)",
    color: "rgba(255,255,255,0.55)",
  },
  rulesSection: {
    marginTop: 22,
    paddingTop: 20,
    borderTop: "1px solid rgba(255,255,255,0.08)",
  },
  ruleGrid: {
    marginTop: 16,
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 12,
  },
  ruleCard: {
    minHeight: 150,
    padding: "16px",
    borderRadius: 18,
    background: "rgba(0,0,0,0.34)",
    border: "1px solid rgba(255,255,255,0.10)",
  },
  ruleNumber: {
    color: "#ff5d5d",
    fontWeight: 900,
    fontSize: 12,
    letterSpacing: 1.5,
  },
  ruleTitle: {
    marginTop: 9,
    color: "#fff",
    fontWeight: 900,
    fontSize: 17,
  },
  ruleBody: {
    marginTop: 8,
    color: "rgba(255,255,255,0.66)",
    lineHeight: 1.5,
    fontSize: 13,
  },
  disclaimer: {
    margin: "20px 0 0",
    color: "rgba(255,255,255,0.46)",
    fontSize: 12,
    lineHeight: 1.55,
  },
  disputeMatchButton: {
    marginTop: 12, border: "1px solid rgba(255, 88, 88, 0.55)", borderRadius: 999,
    background: "rgba(90, 8, 15, 0.62)", color: "#ffd7d7", fontWeight: 900,
    padding: "11px 16px", cursor: "pointer",
  },
  matchUnderReviewBanner: {
    marginTop: 12, border: "1px solid rgba(255, 184, 64, 0.5)", borderRadius: 14,
    background: "rgba(98, 55, 5, 0.34)", color: "#ffe0a0", padding: "12px 14px",
    fontWeight: 800, lineHeight: 1.45,
  },
  adminReviewLink: {
    textDecoration: "none", border: "1px solid rgba(255, 168, 65, 0.5)", borderRadius: 999,
    background: "rgba(86, 48, 8, 0.42)", color: "#ffd27e", fontWeight: 900,
    padding: "10px 14px", display: "inline-flex", alignItems: "center", justifyContent: "center",
  },
  disputeModalBackdrop: {
    position: "fixed", inset: 0, zIndex: 5000, background: "rgba(0, 0, 0, 0.78)",
    display: "flex", alignItems: "center", justifyContent: "center", padding: 18,
  },
  disputeModal: {
    width: "min(720px, 100%)", maxHeight: "90vh", overflowY: "auto",
    border: "1px solid rgba(226, 48, 61, 0.48)", borderRadius: 22,
    background: "linear-gradient(145deg, rgba(34, 8, 12, 0.98), rgba(8, 8, 10, 0.99))",
    boxShadow: "0 28px 90px rgba(0,0,0,0.7)", padding: 22,
  },
  disputeModalHeader: { display: "flex", justifyContent: "space-between", gap: 16, alignItems: "flex-start" },
  disputeModalTitle: { margin: "6px 0 8px", color: "#fff", fontSize: "clamp(1.6rem, 4vw, 2.5rem)" },
  disputeModalSubcopy: { margin: 0, color: "rgba(255,255,255,0.74)", lineHeight: 1.5 },
  disputeModalClose: {
    width: 42, height: 42, flex: "0 0 42px", borderRadius: "50%",
    border: "1px solid rgba(255,255,255,0.18)", background: "rgba(255,255,255,0.08)",
    color: "#fff", fontSize: 24, cursor: "pointer",
  },
  disputeForm: { display: "grid", gap: 16, marginTop: 20 },
  disputeTextarea: {
    width: "100%", marginTop: 8, borderRadius: 14, border: "1px solid rgba(255,255,255,0.18)",
    background: "rgba(0,0,0,0.46)", color: "#fff", padding: 14, resize: "vertical",
    font: "inherit", boxSizing: "border-box",
  },
  fileInput: {
    width: "100%", marginTop: 8, borderRadius: 12, border: "1px solid rgba(255,255,255,0.16)",
    background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.82)",
    padding: 10, boxSizing: "border-box",
  },
  selectedProofList: { display: "grid", gap: 8 },
  selectedProofItem: {
    borderRadius: 10, background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.78)",
    padding: "9px 11px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
  },
  disputeModalActions: { display: "flex", flexWrap: "wrap", justifyContent: "flex-end", gap: 10 },
  disputeSubmitButton: {
    border: "1px solid rgba(255, 82, 82, 0.75)", borderRadius: 999,
    background: "linear-gradient(135deg, #d91522, #85040d)", color: "#fff",
    fontWeight: 900, padding: "12px 18px", cursor: "pointer",
  },
  gamertagDisclosure: {
    marginTop: 8, color: "rgba(255,255,255,0.68)", fontSize: 12, lineHeight: 1.45,
  },
  inviteDetailsGrid: {
    flex: "1 1 100%", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10,
  },
  rosterGameDetails: {
    marginTop: 8, color: "rgba(255,255,255,0.72)", fontSize: 12, lineHeight: 1.4,
  },
  playerDetailsPrompt: {
    marginTop: 14, padding: 14, borderRadius: 16, border: "1px solid rgba(0,190,255,0.28)",
    background: "rgba(0,90,130,0.16)", display: "grid", gap: 12,
  },
  playerDetailsTitle: { color: "#fff", fontWeight: 900, fontSize: 15 },
  playerDetailsCopy: { marginTop: 5, color: "rgba(255,255,255,0.7)", fontSize: 12, lineHeight: 1.45 },
  playerDetailsInputs: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 10 },
  matchLobbyButton: {
    marginTop: 14, border: "1px solid rgba(80,200,255,0.45)", borderRadius: 999,
    background: "linear-gradient(90deg, rgba(0,132,210,0.88), rgba(95,40,190,0.82))", color: "#fff",
    fontWeight: 900, padding: "11px 16px", cursor: "pointer",
  },
  winnerPayoutCard: {
    marginTop: 16, borderRadius: 18, padding: 16, border: "1px solid rgba(255,208,86,0.38)",
    background: "linear-gradient(145deg, rgba(105,70,0,0.28), rgba(20,5,0,0.65))",
    display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap",
  },
  winnerPayoutAmount: { marginTop: 4, color: "#ffd666", fontSize: 30, fontWeight: 1000 },
  winnerPayoutCopy: { marginTop: 6, color: "rgba(255,255,255,0.76)", maxWidth: 720, lineHeight: 1.5, fontSize: 13 },
  payoutStatusMessage: { marginTop: 10, color: "#ffd46b", fontSize: 12, fontWeight: 900, lineHeight: 1.45 },
  payoutActionRow: { display: "flex", flexWrap: "wrap", gap: 9, justifyContent: "flex-end", alignItems: "center" },
  secondaryActionButton: { border: "1px solid rgba(255,255,255,.18)", borderRadius: 999, background: "rgba(255,255,255,.07)", color: "#fff", padding: "10px 14px", fontWeight: 900, cursor: "pointer" },
  championOverlayBackdrop: {
    position: "absolute", inset: 0, zIndex: 30, background: "rgba(0,0,0,0.82)",
    display: "grid", placeItems: "center", padding: "clamp(8px, 1.5vw, 18px)",
  },
  championOverlayCard: {
    width: "min(760px, 88%)", maxHeight: "calc(100% - 8px)", position: "relative", overflowY: "auto",
    borderRadius: "clamp(14px, 2vw, 26px)", padding: "clamp(14px, 3vw, 34px) clamp(14px, 2.5vw, 24px)",
    border: "1px solid rgba(255,214,92,0.54)", textAlign: "center", color: "#fff",
    background: "radial-gradient(circle at 50% 20%, rgba(255,55,55,0.34), transparent 42%), linear-gradient(145deg, rgba(60,8,12,0.98), rgba(5,5,8,0.99))",
    boxShadow: "0 20px 70px rgba(0,0,0,0.8), 0 0 45px rgba(255,190,55,0.18)",
  },
  championOverlayClose: {
    position: "absolute", top: 10, right: 10, width: 36, height: 36, borderRadius: "50%",
    border: "1px solid rgba(255,255,255,0.18)", background: "rgba(255,255,255,0.08)", color: "#fff", fontSize: 22, cursor: "pointer", zIndex: 2,
  },
  championCrown: { fontSize: "clamp(28px, 5vw, 58px)", lineHeight: 1 },
  championTeamName: { margin: "6px 0 4px", fontSize: "clamp(1.5rem, 5vw, 4.25rem)", lineHeight: 1.02, color: "#fff", textTransform: "uppercase" },
  championFinalLine: { color: "#ffd56b", fontWeight: 900, fontSize: "clamp(12px, 1.5vw, 18px)" },
  championRosterLine: { marginTop: 10, color: "rgba(255,255,255,0.82)", fontWeight: 800, fontSize: "clamp(10px, 1.15vw, 15px)" },
  championPrizeLine: { marginTop: 8, color: "#fff", fontWeight: 900, fontSize: "clamp(11px, 1.25vw, 16px)" },
  lobbyBackdrop: {
    position: "fixed", inset: 0, zIndex: 4950, background: "rgba(0,0,0,0.8)", display: "grid", placeItems: "center", padding: 18,
  },
  lobbyModal: {
    width: "min(900px, 100%)", maxHeight: "92vh", overflowY: "auto", borderRadius: 24, padding: 20,
    border: "1px solid rgba(90,185,255,0.36)", background: "linear-gradient(145deg, rgba(16,20,34,0.99), rgba(10,4,12,0.99))",
    boxShadow: "0 30px 100px rgba(0,0,0,0.8)", color: "#fff",
  },
  lobbyHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 },
  lobbyTitle: { margin: "5px 0 0", fontSize: "clamp(1.5rem, 4vw, 2.5rem)" },
  lobbyPrivacyCopy: { marginTop: 7, color: "rgba(255,255,255,0.66)", fontSize: 12, lineHeight: 1.45 },
  lobbyRosters: { marginTop: 18, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12 },
  lobbyRosterCard: { padding: 14, borderRadius: 16, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" },
  lobbyRosterTeam: { fontWeight: 1000, color: "#fff", fontSize: 17, marginBottom: 10 },
  lobbyPlayerRow: { display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 8, alignItems: "center", padding: "8px 0", borderTop: "1px solid rgba(255,255,255,0.07)", fontSize: 12 },
  lobbyPlayerName: { color: "rgba(255,255,255,0.8)", fontWeight: 800 },
  lobbyGamertag: { color: "#78d9ff", fontWeight: 900, overflow: "hidden", textOverflow: "ellipsis" },
  lobbyPlatform: { color: "rgba(255,255,255,0.58)", whiteSpace: "nowrap" },
  lobbyChatBox: { marginTop: 16, maxHeight: 300, overflowY: "auto", display: "grid", gap: 8, padding: 10, borderRadius: 16, background: "rgba(0,0,0,0.32)" },
  lobbyMessageRow: { padding: "10px 12px", borderRadius: 12, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.07)" },
  lobbyMessageMeta: { color: "rgba(255,255,255,0.5)", fontSize: 11, fontWeight: 800 },
  lobbyMessageBody: { marginTop: 4, color: "rgba(255,255,255,0.9)", lineHeight: 1.45, whiteSpace: "pre-wrap" },
  lobbyComposer: { marginTop: 12, display: "grid", gridTemplateColumns: "1fr auto", gap: 10 },
  rulesHelpLink: {
    marginTop: 10, padding: 0, border: 0, background: "transparent", color: "#66c8ff",
    fontSize: 12, fontWeight: 900, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3,
  },
  communicationsPanel: {
    marginTop: 18, padding: 18, borderRadius: 20,
    border: "1px solid rgba(65,165,255,0.28)",
    background: "linear-gradient(145deg, rgba(0,70,115,0.16), rgba(20,4,8,0.55))",
  },
  communicationsHeader: {
    display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap",
  },
  communicationsTitle: {
    margin: "6px 0 0", fontSize: "clamp(22px, 3.5vw, 34px)", fontFamily: 'Georgia, "Times New Roman", serif',
  },
  communicationsCount: {
    minWidth: 190, display: "grid", gap: 3, padding: "12px 14px", borderRadius: 14,
    background: "rgba(0,0,0,0.34)", border: "1px solid rgba(255,255,255,0.1)", color: "#fff",
  },
  communicationTemplateGrid: {
    marginTop: 16, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(245px, 1fr))", gap: 12,
  },
  communicationTemplateCard: {
    padding: 14, borderRadius: 16, background: "rgba(0,0,0,0.34)", border: "1px solid rgba(255,255,255,0.09)",
    display: "grid", alignContent: "start", gap: 8,
  },
  communicationTemplateLabel: { color: "#7dd3ff", fontSize: 11, fontWeight: 1000, letterSpacing: 1.1, textTransform: "uppercase" },
  communicationSubject: { color: "#fff", fontWeight: 900, lineHeight: 1.35 },
  communicationPreviewCopy: { color: "rgba(255,255,255,0.66)", fontSize: 12, lineHeight: 1.5 },
  communicationActions: { marginTop: 4, display: "flex", flexWrap: "wrap", gap: 8 },
  communicationHistory: { marginTop: 16, borderTop: "1px solid rgba(255,255,255,0.09)", paddingTop: 14 },
  communicationHistoryTitle: { color: "rgba(255,255,255,0.74)", fontSize: 12, fontWeight: 900, marginBottom: 8 },
  communicationHistoryRow: {
    display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap",
    padding: "10px 0", borderTop: "1px solid rgba(255,255,255,0.06)", color: "#fff",
  },
  communicationHistoryMeta: { display: "block", marginTop: 3, color: "rgba(255,255,255,0.48)", fontSize: 11 },
  communicationDeliveryBadge: {
    padding: "6px 9px", borderRadius: 999, background: "rgba(40,160,100,0.16)",
    border: "1px solid rgba(90,220,150,0.3)", color: "#a8ffd0", fontSize: 11, fontWeight: 900,
  },
  rulesModalBackdrop: {
    position: "fixed", inset: 0, zIndex: 5200, background: "rgba(0,0,0,0.82)",
    display: "grid", placeItems: "center", padding: 18,
  },
  rulesModal: {
    width: "min(920px, 100%)", maxHeight: "92vh", overflowY: "auto", borderRadius: 24, padding: 20,
    border: "1px solid rgba(65,165,255,0.34)",
    background: "linear-gradient(145deg, rgba(15,18,30,0.995), rgba(19,4,8,0.995))",
    boxShadow: "0 30px 100px rgba(0,0,0,0.82)", color: "#fff",
  },
  rulesModalHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 },
  rulesModalTitle: { margin: "6px 0 6px", fontSize: "clamp(1.7rem, 4vw, 2.8rem)", color: "#fff" },
  rulesModalSubcopy: { margin: 0, color: "rgba(255,255,255,0.68)", lineHeight: 1.5, maxWidth: 760 },
  rulesTabs: {
    marginTop: 18, display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4,
  },
  rulesTab: {
    flex: "0 0 auto", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 999,
    background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.72)", padding: "9px 12px",
    fontWeight: 900, cursor: "pointer",
  },
  rulesTabActive: {
    flex: "0 0 auto", border: "1px solid rgba(70,190,255,0.5)", borderRadius: 999,
    background: "linear-gradient(135deg, rgba(0,130,210,0.85), rgba(100,40,190,0.78))", color: "#fff",
    padding: "9px 12px", fontWeight: 900, cursor: "pointer",
  },
  rulesModalBody: { marginTop: 16, display: "grid", gap: 10 },
  ruleModalSection: {
    padding: 14, borderRadius: 15, background: "rgba(255,255,255,0.045)", border: "1px solid rgba(255,255,255,0.08)",
  },
  ruleModalSectionTitle: { color: "#fff", fontSize: 15, fontWeight: 1000 },
  ruleModalSectionBody: { marginTop: 5, color: "rgba(255,255,255,0.76)", fontSize: 13, lineHeight: 1.62 },
  ruleStep: {
    display: "grid", gridTemplateColumns: "44px 1fr", gap: 12, padding: 14, borderRadius: 15,
    background: "rgba(255,255,255,0.045)", border: "1px solid rgba(255,255,255,0.08)",
  },
  ruleStepNumber: {
    width: 40, height: 40, display: "grid", placeItems: "center", borderRadius: "50%",
    background: "rgba(210,10,28,0.24)", border: "1px solid rgba(255,80,90,0.34)", color: "#ff9a9f", fontWeight: 1000,
  },
  emailPreviewModal: {
    width: "min(760px, 100%)", maxHeight: "92vh", overflowY: "auto", borderRadius: 24, padding: 20,
    border: "1px solid rgba(65,165,255,0.34)", background: "linear-gradient(145deg, rgba(15,18,30,0.995), rgba(19,4,8,0.995))",
    boxShadow: "0 30px 100px rgba(0,0,0,0.82)", color: "#fff",
  },
  emailPreviewCard: {
    marginTop: 16, padding: 18, borderRadius: 18, background: "linear-gradient(145deg,#171922,#090a0f)",
    border: "1px solid rgba(255,255,255,0.12)",
  },
  emailPreviewSubject: { color: "#ff9ba2", fontWeight: 900, lineHeight: 1.45 },
  emailPreviewGreeting: { margin: "16px 0 8px", fontSize: 16, color: "#fff" },
  emailPreviewText: { margin: 0, color: "rgba(255,255,255,0.76)", lineHeight: 1.6 },
  emailPreviewDetails: {
    marginTop: 14, padding: 14, borderRadius: 14, display: "grid", gap: 5, background: "rgba(0,0,0,0.34)",
    border: "1px solid rgba(255,255,255,0.09)", color: "rgba(255,255,255,0.8)", fontSize: 13,
  },
  emailPreviewList: { margin: "14px 0 0", paddingLeft: 22, color: "rgba(255,255,255,0.78)", lineHeight: 1.65, fontSize: 13 },
  emailPreviewButton: {
    marginTop: 16, display: "inline-flex", padding: "11px 16px", borderRadius: 999,
    background: "linear-gradient(180deg,#e10613,#8c0000)", color: "#fff", fontWeight: 900,
  },

  registrationFlowSteps: {
    marginTop: 16,
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
    gap: 10,
  },
  registrationFlowStep: {
    display: "grid",
    gridTemplateColumns: "38px 1fr",
    gap: 10,
    alignItems: "start",
    padding: 12,
    borderRadius: 14,
    background: "rgba(255,255,255,0.045)",
    border: "1px solid rgba(255,255,255,0.085)",
  },
  flowStepBadge: {
    width: 34,
    height: 34,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    flex: "0 0 34px",
    background: "rgba(208,10,28,0.24)",
    border: "1px solid rgba(255,82,92,0.36)",
    color: "#ff9ca3",
    fontWeight: 1000,
    fontSize: 12,
  },
  flowSectionTitle: {
    color: "#fff",
    fontWeight: 1000,
    fontSize: 14,
    lineHeight: 1.25,
  },
  flowStepCopy: {
    marginTop: 4,
    color: "rgba(255,255,255,0.64)",
    fontSize: 11,
    lineHeight: 1.45,
  },
  inviteFlowPanel: {
    flex: "1 1 100%",
    display: "grid",
    gap: 12,
    padding: 14,
    borderRadius: 16,
    border: "1px solid rgba(76,178,255,0.25)",
    background: "linear-gradient(145deg, rgba(0,80,125,0.12), rgba(12,4,8,0.42))",
  },
  flowStatusReady: {
    padding: 13,
    borderRadius: 14,
    border: "1px solid rgba(88,220,150,0.3)",
    background: "rgba(0,105,68,0.14)",
  },
  flowStatusNeeded: {
    padding: 13,
    borderRadius: 14,
    border: "1px solid rgba(255,195,75,0.3)",
    background: "rgba(110,70,0,0.14)",
  },
  flowStatusLabel: {
    color: "#ff9ca3",
    fontSize: 10,
    fontWeight: 1000,
    letterSpacing: 1.1,
    textTransform: "uppercase",
  },
  flowStatusTitle: {
    marginTop: 4,
    color: "#fff",
    fontSize: 20,
    fontWeight: 1000,
    lineHeight: 1.1,
  },
  flowStatusCopy: {
    marginTop: 6,
    color: "rgba(255,255,255,0.72)",
    fontSize: 12,
    lineHeight: 1.5,
  },
  flowSectionHeader: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
  },
  registrationEligibilityReady: {
    gridColumn: "1 / -1",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 14,
    flexWrap: "wrap",
    padding: 14,
    borderRadius: 16,
    border: "1px solid rgba(88,220,150,0.3)",
    background: "linear-gradient(145deg, rgba(0,105,68,0.15), rgba(10,4,8,0.5))",
  },
  registrationEligibilityNeeded: {
    gridColumn: "1 / -1",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 14,
    flexWrap: "wrap",
    padding: 14,
    borderRadius: 16,
    border: "1px solid rgba(255,195,75,0.3)",
    background: "linear-gradient(145deg, rgba(110,70,0,0.16), rgba(10,4,8,0.5))",
  },
  registrationResumeBanner: {
    gridColumn: "1 / -1",
    padding: "11px 13px",
    borderRadius: 13,
    border: "1px solid rgba(88,220,150,0.32)",
    background: "rgba(0,105,68,0.16)",
    color: "#a8ffd0",
    fontSize: 12,
    fontWeight: 900,
    lineHeight: 1.45,
  },
  registrationFinePrint: {
    gridColumn: "1 / -1",
    color: "rgba(255,255,255,0.48)",
    fontSize: 11,
    lineHeight: 1.5,
  },
  registrationPolicyNotice: {
    marginTop: 14,
    padding: "14px 16px",
    borderRadius: 16,
    border: "1px solid rgba(255,92,102,0.28)",
    background: "linear-gradient(145deg, rgba(120,0,12,0.18), rgba(8,8,12,0.58))",
    color: "rgba(255,255,255,0.82)",
    fontSize: 13,
    lineHeight: 1.6,
  },
  schedulePanel: {
    marginTop: 16,
    padding: 16,
    borderRadius: 18,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "linear-gradient(145deg, rgba(72,8,15,0.28), rgba(8,8,12,0.62))",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    flexWrap: "wrap",
  },
  scheduleTitle: {
    margin: "6px 0 0",
    color: "#fff",
    fontSize: 21,
    fontWeight: 1000,
    fontFamily: 'Georgia, "Times New Roman", serif',
  },
  scheduleCopy: {
    margin: "8px 0 0",
    color: "rgba(255,255,255,0.66)",
    fontSize: 12,
    lineHeight: 1.5,
  },
  scheduleControls: {
    flex: "1 1 360px",
    display: "flex",
    gap: 10,
    alignItems: "center",
    justifyContent: "flex-end",
    flexWrap: "wrap",
  },
  scheduleInput: {
    minWidth: 230,
    flex: "1 1 230px",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.18)",
    background: "rgba(0,0,0,0.58)",
    color: "#fff",
    padding: "11px 12px",
    fontWeight: 800,
  },

  payoutEligibilityCard: {
    marginTop: 14, padding: 16, borderRadius: 18, border: "1px solid rgba(255,195,75,0.33)",
    background: "linear-gradient(145deg, rgba(110,70,0,0.18), rgba(20,4,8,0.58))", display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center", justifyContent: "space-between",
  },
  payoutEligibilityReadyCard: {
    marginTop: 14, padding: 16, borderRadius: 18, border: "1px solid rgba(90,220,150,0.32)",
    background: "linear-gradient(145deg, rgba(0,110,70,0.16), rgba(20,4,8,0.58))", display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center", justifyContent: "space-between",
  },
  payoutEligibilityTitle: { margin: "6px 0 8px", fontSize: "clamp(20px, 3vw, 32px)", color: "#fff", fontFamily: 'Georgia, "Times New Roman", serif' },
  eligibilityWarning: { marginTop: 12, padding: "10px 12px", borderRadius: 13, border: "1px solid rgba(255,210,80,0.3)", background: "rgba(120,75,0,0.2)", color: "#ffe08a", fontSize: 12, fontWeight: 900, lineHeight: 1.45 },
  payoutReadyText: { marginTop: 8, color: "#9dffbf", fontSize: 11, fontWeight: 1000, letterSpacing: 0.4, textTransform: "uppercase" },
  payoutNeededText: { marginTop: 8, color: "#ffd46b", fontSize: 11, fontWeight: 1000, letterSpacing: 0.4, textTransform: "uppercase" },
  adminPayoutWarning: { marginTop: 6, color: "#ffd46b", fontSize: 12, fontWeight: 900 },

};
